import type { Device as TwilioDevice } from '@twilio/voice-sdk';
import io, { type Socket } from 'socket.io-client';
import { TWILIO_API_URL, TWILIO_SOCKET_URL } from '../config/twilio';
import { resolveApiBaseUrl, resolveSocketOrigin } from '../shared/config/api';
import { isAgentPhoneWindow, publishCallState, publishScreenPop } from './agentPhone/agentPhoneBridge';

const API_BASE_URL = resolveApiBaseUrl();

let twilioVoiceSdkPromise: Promise<typeof import('@twilio/voice-sdk')> | null = null;

async function loadTwilioVoiceSdk() {
  if (!twilioVoiceSdkPromise) {
    twilioVoiceSdkPromise = import('@twilio/voice-sdk');
  }
  return twilioVoiceSdkPromise;
}

export interface CallState {
  // Call status states
  status: 'idle' | 'ringing' | 'in-call';
  
  // Connection states
  isConnected: boolean;
  isMuted: boolean;
  isSpeakerOn: boolean;
  isOnHold: boolean;
  callDuration: number;
  direction: 'none' | 'inbound' | 'outbound';
  
  // Caller information
  callerInfo: {
    name: string;
    phone: string;
  } | null;
  clientId: string | null;
  clientName: string | null;
  clientPhone: string | null;
  
  // Call metadata
  callSid: string | null;
  queueTime: number;
  
  // Channel (direct, whatsapp, app)
  channel: 'direct' | 'whatsapp' | 'app';
}

export interface CallTimer {
  startTime: Date;
  queueTime: number; // Time spent in queue (seconds)
  callTime: number; // Time spent in actual call (seconds)
  totalTime: number; // Total time from queue start
}

export class WebRTCCallService {
  private static instance: WebRTCCallService | null = null;
  private device: TwilioDevice | null = null;
  private call: any = null;
  private socket: any = null;
  private callTimer: CallTimer | null = null;
  private timerInterval: NodeJS.Timeout | null = null;
  private ringingAudio: HTMLAudioElement | null = null;
  private agentId: string | null = null;
  private agentName: string | null = null;
  private agentClientType: 'web' | 'phone' = 'web';
  private isInitialized: boolean = false;
  
  // Resilience handlers
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private static readonly HEARTBEAT_INTERVAL_MS = 25000; // 25 seconds (for connection health monitoring only)
  
  private callState: CallState = {
    status: 'idle',
    isConnected: false,
    isMuted: false,
    isSpeakerOn: true,
    isOnHold: false,
    callDuration: 0,
    direction: 'none',
    callerInfo: null,
    clientId: null,
    clientName: null,
    clientPhone: null,
    callSid: null,
    queueTime: 0,
    channel: 'direct' // Default to direct, will be updated from task attributes
  };

  private callStateListeners: ((state: CallState) => void)[] = [];
  private timerListeners: ((timer: CallTimer) => void)[] = [];
  private incomingCallTimeout: NodeJS.Timeout | null = null;
  private outboundEndTimer: ReturnType<typeof setTimeout> | null = null;
  /** Socket ring id from POST /app-voice/ring-client — used to ignore stale decline/end events. */
  private outboundAppRingCallId: string | null = null;
  /** Prevents Twilio accept/disconnect churn from reopening UI after agent clicks Hang Up. */
  private hangupRequested = false;
  /** Blocks late socket events from re-showing the overlay right after hang up. */
  private hangupUiLockUntil = 0;
  /** Invalidates in-flight ClientChats handleAppCall after agent declines during ring prep. */
  private outboundPlaceToken = 0;
  private readonly cancelledOutboundPlaceTokens = new Set<number>();
  /** When beginOutboundCallSession started (stale prep cleanup). */
  private outboundPrepStartedAt: number | null = null;
  /** Mobile client accepted via socket — allow short bridge grace before clearing UI. */
  private outboundAppClientAnswered = false;
  /**
   * True only when THIS browser tab accepted the current incoming call.
   * Same agent is registered on web + mobile with one Twilio identity, so a
   * `call:accepted` (reservation.accepted) socket event fans out to both. Without
   * this flag the web tab would flip to in-call when the agent answered on mobile.
   */
  private acceptedLocally = false;
  private lastProcessedCallSid: string | null = null;
  /** Grace after mobile accepts (socket) while admin Twilio leg bridges. */
  private static readonly APP_OUTBOUND_BRIDGE_GRACE_MS = 3_000;
  private static readonly APP_OUTBOUND_EARLY_PREP_MS = 8_000;
  private static readonly OUTBOUND_PREP_MAX_MS = 120_000;
  private static readonly DEVICE_IDLE_WAIT_MS = 10_000;
  private lastProcessedEventTime: number = 0;

  private static readonly AUTO_ACCEPT_ENV: string =
    (typeof import.meta !== 'undefined' &&
      (import.meta as any)?.env &&
      (import.meta as any).env.VITE_TWILIO_AUTO_ACCEPT) ??
    'false';

  private constructor() {
    console.log('🔧 WebRTCCallService constructor called - SINGLETON');
    // Don't initialize here - wait for explicit init call
  }

  private extractIdentityFromToken(token: string): string | null {
    try {
      const [, payload] = token.split('.');
      if (!payload) {
        return null;
      }

      const decoded = JSON.parse(atob(payload));
      const identity =
        decoded?.grants?.identity ??
        decoded?.identity ??
        decoded?.sub ??
        null;

      return typeof identity === 'string' ? identity : null;
    } catch (error) {
      console.warn('⚠️ Unable to extract identity from Twilio token:', error);
      return null;
    }
  }

  private logDeviceRegistrationStatus(stage: string): void {
    console.log(`📟 [TwilioDevice][${stage}] state:`, this.device?.state);
    console.log(`📟 [TwilioDevice][${stage}] isInitialized:`, this.isInitialized);
    console.log(`📟 [TwilioDevice][${stage}] agentId:`, this.agentId);
    console.log(`📟 [TwilioDevice][${stage}] agentName:`, this.agentName);
  }

  private normalizeOutboundNumber(rawNumber: string): string | null {
    if (!rawNumber) {
      return null;
    }

    const trimmed = rawNumber.trim();
    if (trimmed.length === 0) {
      return null;
    }

    if (trimmed.toLowerCase().startsWith('client:')) {
      return trimmed;
    }

    const digits = trimmed.replace(/\D/g, '');
    if (digits.length < 10) {
      return null;
    }

    if (trimmed.startsWith('+')) {
      return `+${digits}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    if (digits.length === 10) {
      return `+1${digits}`;
    }

    return `+${digits}`;
  }

  public static getInstance(): WebRTCCallService {
    if (!WebRTCCallService.instance) {
      WebRTCCallService.instance = new WebRTCCallService();
    }
    return WebRTCCallService.instance;
  }

  // Reset singleton instance (for testing or cleanup)
  public static resetInstance(): void {
    if (WebRTCCallService.instance) {
      WebRTCCallService.instance.destroy();
      WebRTCCallService.instance = null;
    }
  }

  private initializeSocket(): void {
    if (this.socket && this.socket.connected) {
      console.log('🔌 Socket already initialized and connected, skipping...');
      return;
    }
    
    // If socket exists but not connected, disconnect it first
    if (this.socket && !this.socket.connected) {
      console.log('🔌 Disconnecting existing socket before reinitializing...');
      this.socket.disconnect();
      this.socket = null;
    }
    
    const twilioServerUrl = TWILIO_SOCKET_URL;
    console.log('🔌 Initializing Socket.IO connection to:', twilioServerUrl);
    console.log('🔍 DEBUG: Using Twilio socket URL:', twilioServerUrl);
    
    this.socket = io(twilioServerUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      timeout: 10000,
      forceNew: true
    });
    
    console.log('🔍 DEBUG: Socket created:', !!this.socket);
    console.log('🔍 DEBUG: Socket connecting:', this.socket.connecting);
    console.log('🔍 DEBUG: Socket connected:', this.socket.connected);

    this.socket.on('connect', () => {
      console.log('🔌 WebRTC Socket connected to:', twilioServerUrl);
      console.log('🔌 Socket ID:', this.socket?.id);
      
      // If agent is already identified, send agent:identify event immediately
      if (this.agentId && this.agentName) {
        console.log('🔌 Socket connected - sending delayed agent:identify event');
        this.socket.emit('agent:identify', {
          userId: this.agentId,
          agentName: this.agentName,
          clientType: this.socketClientType()
        });
        console.log('🔌 Delayed agent identification sent to server');
      }
    });

    this.socket.on('disconnect', (reason: any) => {
      console.log('🔌 WebRTC Socket disconnected:', reason);
      console.log('🔍 DEBUG: Socket disconnected - agent will not receive calls');
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('❌ Socket connection error:', error);
      console.error('❌ Connection URL:', twilioServerUrl);
      console.error('🔍 DEBUG: Socket connection failed - this is why agent is not found');
    });

    // Handle reconnection - re-identify agent
    this.socket.on('reconnect', () => {
      console.log('🔌 Socket reconnected, re-identifying agent...');
      if (this.agentId && this.agentName) {
        this.socket.emit('agent:identify', {
          userId: this.agentId,
          agentName: this.agentName,
          clientType: this.socketClientType()
        });
      }
    });

    // Handle heartbeat acknowledgement from server
    this.socket.on('agent:heartbeat-ack', (data: { timestamp: string }) => {
      console.log('🫀 Heartbeat acknowledged by server:', data.timestamp);
    });

    // Listen for call events - organized by state
    this.socket.on('call:incoming', (data: any) => {
      console.log('📞 [Socket:call:incoming] ========== INCOMING CALL EVENT ==========');
      console.log('📞 [Socket:call:incoming] Full data:', JSON.stringify(data, null, 2));
      console.log('📞 [Socket:call:incoming] Current agent ID:', this.agentId);
      console.log('📞 [Socket:call:incoming] Socket connected:', this.socket.connected);
      console.log('📞 [Socket:call:incoming] Current call state before update:', this.callState.status);

      if (this.isActiveOutboundSession()) {
        console.log('⚠️ [Socket:call:incoming] Ignored — active outbound call in progress');
        return;
      }
      
      // Deduplicate events - prevent processing same call multiple times
      const currentTime = Date.now();
      const timeDiff = currentTime - this.lastProcessedEventTime;
      const isDuplicate = this.lastProcessedCallSid === data.callSid && timeDiff < 1000; // 1 second window
      
      if (isDuplicate) {
        console.log('⚠️ [Socket:call:incoming] Duplicate event ignored for callSid:', data.callSid);
        return;
      }
      
      this.lastProcessedCallSid = data.callSid;
      this.lastProcessedEventTime = currentTime;
      
      console.log('✅ [Socket:call:incoming] Processing incoming call - calling handleRingingState');
      this.handleRingingState(data);
      console.log('📞 [Socket:call:incoming] ===========================================');
    });

    this.socket.on('call:accepted', (data: any) => {
      console.log('✅ [Socket:call:accepted] Event received!');
      console.log('✅ [Socket:call:accepted] Full data:', JSON.stringify(data, null, 2));
      console.log('✅ [Socket:call:accepted] Current call state before update:', this.callState.status);
      // Legacy Stream incoming_ring socket events — ignore (admin uses Twilio only).
      if (data?.callId && String(data.callId).startsWith('incoming_ring:')) {
        console.log('✅ [Socket:call:accepted] Stream incoming_ring wake — ignored by Twilio UI');
        return;
      }
      if (this.isActiveOutboundSession() && this.callState.channel === 'app') {
        console.log('✅ [Socket:call:accepted] Outbound app — client answered, keeping outbound session');
        this.handleOutboundAppClientAnswered(data);
        return;
      }

      // Same agent is signed in on web + mobile (one Twilio identity), so this
      // reservation.accepted event reaches both. If THIS tab did not accept the
      // call (agent answered on another device such as mobile app), dismiss the ring
      // and do NOT flip to in-call!
      if (!this.acceptedLocally && this.callState.direction !== 'outbound') {
        console.log('✅ [Socket:call:accepted] Answered on another device (mobile app) — dismissing web ring');
        this.dismissRingBecauseAnsweredElsewhere();
        return;
      }

      console.log('✅ [Socket:call:accepted] Processing call accepted - calling handleInCallState');
      this.handleInCallState(data);
    });

    this.socket.on('call:accepted-from-device', (data: { device?: string; callSid?: string }) => {
      console.log('📞 [Socket:call:accepted-from-device] Call accepted on device:', data);
      if (data?.device && data.device !== 'web') {
        console.log(`📞 Call was answered on ${data.device} — dismissing web ring`);
        this.dismissRingBecauseAnsweredElsewhere();
      }
    });

    this.socket.on('call:declined', (data: { callId?: string; targetId?: string }) => {
      console.log('📞 [Socket:call:declined] Client declined:', data);
      this.handleClientDeclinedCall(data);
    });

    this.socket.on('call:ended', (data: any) => {
      console.log('📞 CALL ENDED: Call ended via socket:', data);
      this.handleCallEnded(data);
    });


    this.socket.on('call:client-hangup', (data: any) => {
      console.log('🔥🔥🔥 SOCKET EVENT: call:client-hangup received 🔥🔥🔥');
      console.log('📞 CLIENT HANGUP: Client hung up:', data);
      console.log('📞 CLIENT HANGUP: Current call state when event received:', this.callState.status);
      console.log('📞 CLIENT HANGUP: Timestamp:', new Date().toISOString());
      this.handleClientHangup(data);
    });

    // Immediate client-hang-up signal: client app emits call:cancelled with the Twilio
    // Call SID (starts with "CA") when the user presses End — this fires ~2-5 s earlier
    // than the Twilio status-callback HTTP path that triggers call:client-hangup.
    this.socket.on('call:cancelled', (data: any) => {
      const rawCallId = String(data?.callId ?? '');
      if (rawCallId.startsWith('CA') && rawCallId.length > 10 && this.callState.status === 'ringing') {
        console.log('📞 [call:cancelled] Twilio SID immediate hangup signal — clearing ring', rawCallId);
        this.clearCallBecauseRemoteEnded('call:cancelled (client hangup - immediate)', {
          callSid: rawCallId,
        });
      }
    });

    // Listen for hold/unhold events
    this.socket.on('call:hold', (data: any) => {
      console.log('⏸️ [Socket:call:hold] Call placed on hold:', data);
      if (this.callState.callSid === data.callSid) {
        this.updateCallState({ isOnHold: true });
      }
    });

    this.socket.on('call:unhold', (data: any) => {
      console.log('▶️ [Socket:call:unhold] Call resumed from hold:', data);
      if (this.callState.callSid === data.callSid) {
        this.updateCallState({ isOnHold: false });
      }
    });

    // Listen for reservation timeout - agent didn't pick up in time
    // This keeps the frontend banner in sync with the actual Twilio reservation state
    this.socket.on('call:reservation-timeout', (data: any) => {
      console.log('⏰ RESERVATION TIMEOUT: Agent did not pick up in time:', data);
      console.log('⏰ Current call state:', this.callState.status);
      console.log('⏰ Current call SID:', this.callState.callSid);
      
      // Only dismiss inbound queue reservations — not outbound app/PSTN dials (no callSid yet).
      if (this.callState.status === 'ringing' &&
          this.callState.direction === 'inbound' &&
          (data.callSid === this.callState.callSid || data.agentId === this.agentId)) {
        console.log('⏰ Dismissing call banner - reservation timed out');
        
        // IMPORTANT: Reject the Twilio call to stop the ringing sound
        if (this.call) {
          try {
            console.log('⏰ Rejecting Twilio call to stop ringing sound...');
            this.call.reject();
            console.log('⏰ Twilio call rejected - sound should stop');
          } catch (error) {
            console.warn('⏰ Error rejecting call:', error);
          }
        }
        
        // Clear timeout if any
        if (this.incomingCallTimeout) {
          clearTimeout(this.incomingCallTimeout);
          this.incomingCallTimeout = null;
        }
        
        // Reset call state
        this.callState = {
          status: 'idle',
          isConnected: false,
          isMuted: false,
          isSpeakerOn: false,
          isOnHold: false,
          callDuration: 0,
          direction: 'none',
          callerInfo: null,
          clientId: null,
          clientName: null,
          clientPhone: null,
          callSid: null,
          queueTime: 0,
          channel: 'direct'
        };
        
        // Clear the call reference
        this.call = null;
        
        // Notify listeners to update UI
        this.notifyCallStateListeners();
        console.log('⏰ Call banner dismissed due to reservation timeout');
      }
    });
  }

  // =========================================================================
  // RESILIENCE HANDLERS
  // =========================================================================

  /**
   * Setup resilience handlers for REAL disconnection scenarios only
   * - Heartbeat: connection health monitoring (does NOT mark offline)
   * - beforeunload: graceful cleanup on TAB CLOSE (real disconnection)
   * - online/offline: detect INTERNET DISCONNECTION (real disconnection)
   * - visibilitychange: keep connection alive when tab hidden (NOT a disconnection)
   * 
   * IMPORTANT: We do NOT mark agents offline for:
   * - Background tabs
   * - Different tabs open
   * - Working on phone/Word/Adobe
   * 
   * We ONLY mark offline for:
   * - Tab close (beforeunload)
   * - Internet disconnect (offline event)
   * - Actual socket disconnect
   */
  private setupResilienceHandlers(): void {
    console.log('🛡️ [Resilience] Setting up resilience handlers for REAL disconnections only...');

    // 1. Heartbeat interval - OPTIONAL, for telemetry only (does NOT mark offline)
    // Socket.IO ping/pong handles dead connection detection
    // Presence lease is refreshed on agent:identify and socket events
    // Heartbeats are optional and can be removed if not needed
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    // Optional: Keep heartbeat for telemetry, but it's not required
    // Socket.IO ping/pong + presence lease refresh on events is sufficient
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected && this.agentId && navigator.onLine) {
        // Optional: Send heartbeat for telemetry (refreshes presence lease)
        this.socket.emit('agent:heartbeat');
      }
    }, WebRTCCallService.HEARTBEAT_INTERVAL_MS);
    console.log(`🫀 [Resilience] Heartbeat interval started (${WebRTCCallService.HEARTBEAT_INTERVAL_MS}ms) - OPTIONAL, for telemetry only`);

    // 2. beforeunload handler - TAB CLOSE (real disconnection)
    // IMPORTANT: Show warning if there's an active call to prevent accidental refresh
    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      const hasActiveCall = this.callState.status !== 'idle';
      
      // If there's an active call (ringing or in-call), show warning
      if (hasActiveCall) {
        // Modern browsers ignore custom messages, but still show a warning
        e.preventDefault();
        e.returnValue = ''; // Required for modern browsers
      }
      
      console.log('👋 [Resilience] TAB CLOSE detected (beforeunload) - sending graceful leave');
      
      // Always send graceful leave event to server (regardless of call state)
      if (this.socket?.connected && this.agentId) {
        // Use sendBeacon for reliable delivery during page unload
        const leaveData = JSON.stringify({ userId: this.agentId });
        const apiUrl = resolveSocketOrigin();
        
        // Try navigator.sendBeacon first (most reliable during unload)
        if (navigator.sendBeacon) {
          try {
            const blob = new Blob([leaveData], { type: 'application/json' });
            navigator.sendBeacon(`${apiUrl}/api/twilio/workers/${this.agentId}/status`, blob);
            console.log('👋 [Resilience] Sent leave via sendBeacon');
          } catch (error) {
            console.warn('👋 [Resilience] sendBeacon failed:', error);
          }
        }
        
        // Also emit socket event (may or may not arrive)
        this.socket.emit('agent:leave', { userId: this.agentId });
      }

      // Note: We don't call endCall() here because:
      // 1. If user cancels refresh, we don't want to end the call
      // 2. If user confirms refresh, the page will unload and Twilio Device will be destroyed anyway
      // The call will be dropped naturally when the page unloads
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    console.log('👋 [Resilience] beforeunload handler registered (for TAB CLOSE detection)');

    // 3. online/offline handlers - INTERNET DISCONNECTION (real disconnection)
    this.onlineHandler = () => {
      console.log('🌐 [Resilience] Internet connection restored (online event)');
      // Reconnect socket if disconnected
      if (this.socket && !this.socket.connected && this.agentId) {
        console.log('🌐 [Resilience] Reconnecting socket after internet restore...');
        this.socket.connect();
      } else if (this.socket?.connected && this.agentId) {
        // Re-identify when connection is restored (refreshes presence lease)
        console.log('🌐 [Resilience] Re-identifying agent after internet restore');
        this.socket.emit('agent:identify', {
          userId: this.agentId,
          agentName: this.agentName,
          clientType: this.socketClientType()
        });
      }
    };

    this.offlineHandler = () => {
      console.log('📴 [Resilience] Internet connection lost (offline event)');
      // Note: Socket will automatically disconnect when internet is lost
      // The socket disconnect event will handle marking agent offline
      // We just log it here for debugging
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
    console.log('🌐 [Resilience] online/offline handlers registered (for INTERNET DISCONNECTION detection)');

    // 4. visibilitychange handler - keep connection alive when tab hidden (NOT a disconnection)
    // This is for keeping the connection healthy, NOT for marking offline
    this.visibilityChangeHandler = async () => {
      if (document.hidden) {
        console.log('😴 [Resilience] Tab became hidden (NOT marking offline - agent may be on different tab/phone/Word/Adobe)');
        // Do nothing - agent stays online even if tab is hidden
      } else {
        console.log('👁️ [Resilience] Tab became visible again');
        // When tab becomes visible, refresh presence lease immediately
        // This catches up on any missed presence updates while tab was in background
        if (this.socket?.connected && this.agentId && navigator.onLine) {
          console.log('👁️ [Resilience] Tab visible - refreshing presence lease');
          // Re-identify to refresh presence lease in Redis
          this.socket.emit('agent:identify', {
            userId: this.agentId,
            agentName: this.agentName,
            clientType: this.socketClientType()
          });
        } else if (this.socket && !this.socket.connected && this.agentId && navigator.onLine) {
          console.log('👁️ [Resilience] Socket disconnected, attempting reconnect...');
          this.socket.connect();
        }
        
        // CRITICAL: Also check if Twilio Device needs re-registration
        // This prevents the "No incoming call available" bug after long idle
        if (this.device && this.device.state !== 'registered' && navigator.onLine) {
          console.log('👁️ [Resilience] Twilio Device not registered, attempting re-registration...');
          try {
            await this.device.register();
            console.log('✅ [Resilience] Device re-registered on tab visibility');
          } catch (error) {
            console.error('❌ [Resilience] Failed to re-register device on visibility:', error);
            // Try refreshing token first
            try {
              const response = await fetch(`${TWILIO_API_URL}/tokens/generate`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ userId: this.getCurrentUserId() })
              });
              
              if (response.ok) {
                const data = await response.json();
                await this.device?.updateToken(data.token);
                await this.device?.register();
                console.log('✅ [Resilience] Device token refreshed and re-registered');
              }
            } catch (tokenError) {
              console.error('❌ [Resilience] Failed to refresh token on visibility:', tokenError);
            }
          }
        } else if (this.device?.state === 'registered') {
          console.log('👁️ [Resilience] Twilio Device is already registered ✓');
        }

        this.reclaimStaleCallStateIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    console.log('👁️ [Resilience] visibilitychange handler registered (for connection health, NOT for marking offline)');

    console.log('🛡️ [Resilience] All resilience handlers set up successfully');
    console.log('ℹ️ [Resilience] Agents will ONLY be marked offline for: TAB CLOSE, INTERNET DISCONNECT, or ACTUAL SOCKET DISCONNECT');
  }

  /**
   * Cleanup resilience handlers
   */
  private cleanupResilienceHandlers(): void {
    console.log('🧹 [Resilience] Cleaning up resilience handlers...');

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('🫀 [Resilience] Heartbeat interval cleared');
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
      console.log('👋 [Resilience] beforeunload handler removed');
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
      console.log('👁️ [Resilience] visibilitychange handler removed');
    }

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
      console.log('🌐 [Resilience] online handler removed');
    }

    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
      console.log('📴 [Resilience] offline handler removed');
    }

    console.log('🧹 [Resilience] All resilience handlers cleaned up');
  }

  /** Wait until Twilio Device.state === 'registered' (register() can resolve before state updates). */
  async waitUntilRegistered(timeoutMs = 10000): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this.device?.state === 'registered') {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    throw new Error(
      `Voice device did not register within ${Math.round(timeoutMs / 1000)} seconds. Allow microphone access and try again.`,
    );
  }

  /** Re-register or throw with a clear message for outbound calls. */
  async ensureDeviceRegistered(): Promise<void> {
    if (this.isDeviceReady()) {
      return;
    }

    if (this.device) {
      console.log('📱 Voice device not registered — re-registering…', this.device.state);
      try {
        await this.device.register();
        await this.waitUntilRegistered(8000);
      } catch (err) {
        console.warn('📱 Re-register failed:', err);
      }
    }

    if (!this.isDeviceReady()) {
      throw new Error(
        'Voice device is not ready. In the top bar, set your agent status to Available and allow microphone access, then try again.',
      );
    }
  }

  /**
   * Keep the Twilio contact-center socket connected and refresh agent presence.
   * Safe to call repeatedly (e.g. after reconnect or when device was already registered).
   */
  ensureSocketAndAgentIdentified(agentId?: string, agentName?: string): void {
    if (agentId) {
      this.agentId = agentId;
      this.agentName = agentName || this.agentName || 'Agent';
    }
    if (!this.agentId) {
      return;
    }

    if (!this.socket) {
      this.initializeSocket();
      return;
    }

    if (!this.socket.connected) {
      if (!this.socket.active) {
        this.socket.connect();
      }
      return;
    }

    this.socket.emit('agent:identify', {
      userId: this.agentId,
      agentName: this.agentName || 'Agent',
      clientType: this.socketClientType(),
    });
  }

  /**
   * Fetch token (if needed), register Device, and ensure socket presence for inbound/outbound calls.
   */
  /**
   * Register or refresh the Twilio device for Agent Phone outbound/inbound calls.
   * Always applies a fresh access token (required after navigation from chats).
   */
  async prepareAgentPhoneDevice(
    agentId: string,
    agentName: string,
    token: string,
  ): Promise<void> {
    this.agentId = agentId;
    this.agentName = agentName || 'Agent';
    this.agentClientType = 'phone';

    if (!this.socket) {
      this.initializeSocket();
    }

    if (this.device && this.isInitialized) {
      try {
        await this.device.updateToken(token);
        await this.ensureDeviceRegistered();
        this.ensureSocketAndAgentIdentified(agentId, agentName);
        return;
      } catch (error) {
        console.warn('[WebRTC] Token update failed — reinitializing device', error);
        this.destroy();
      }
    }

    await this.initializeDevice(token, agentId, agentName, 'phone');
    await this.ensureDeviceRegistered();
    this.ensureSocketAndAgentIdentified(agentId, agentName);
  }

  async ensureReadyForCalls(
    agentId: string,
    agentName: string,
    token?: string,
    clientType: 'web' | 'phone' = 'web',
  ): Promise<void> {
    this.agentId = agentId;
    this.agentName = agentName || 'Agent';

    if (!this.socket) {
      this.initializeSocket();
    }
    this.ensureSocketAndAgentIdentified();

    let voiceToken = token;
    if (!voiceToken) {
      const response = await fetch(`${TWILIO_API_URL}/tokens/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ userId: agentId }),
      });

      if (!response.ok) {
        let message = 'Failed to fetch Twilio voice token';
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      const data = await response.json();
      if (!data?.token) {
        throw new Error('Twilio voice token was not returned by the server');
      }
      voiceToken = data.token;
    }

    await this.initializeDevice(voiceToken!, agentId, agentName, clientType);
    await this.ensureDeviceRegistered();
  }

  // Initialize Twilio Device with token
  async initializeDevice(
    token: string,
    agentId?: string,
    agentName?: string,
    clientType: 'web' | 'phone' = 'web',
  ): Promise<void> {
    try {
      if (this.device && this.isInitialized) {
        if (this.device.state === 'registered') {
          console.log('🔧 Device already registered — ensuring socket + presence');
          this.agentClientType = clientType;
          if (!this.socket) {
            this.initializeSocket();
          }
          this.ensureSocketAndAgentIdentified(agentId, agentName);
          if (this.agentId && !this.heartbeatInterval) {
            this.setupResilienceHandlers();
          }
          return;
        }
        console.log('🔧 Device initialized but not registered — re-registering');
        await this.device.register();
        await this.waitUntilRegistered(8000);
        return;
      }

      if (this.device && !this.isInitialized) {
        console.log('🔧 Destroying existing device before reinitializing...');
        this.device.destroy();
        this.device = null;
      }
      
      // Initialize socket first if not done
      if (!this.socket) {
        this.initializeSocket();
      }
      
      console.log('🔧 Initializing Twilio Device...');
      
      this.agentClientType = clientType;

      // Store agent info
      if (agentId) {
        this.agentId = agentId;
        this.agentName = agentName || 'Agent';
        console.log(`🔑 Agent identified: ${this.agentName} (${this.agentId})`);
        
        // Identify agent to server (will be sent when socket connects)
        console.log('🔍 DEBUG: Agent identified, will send agent:identify when socket connects');
        console.log('🔍 DEBUG: Socket exists:', !!this.socket);
        console.log('🔍 DEBUG: Socket connected:', this.socket?.connected);
        console.log('🔍 DEBUG: Socket connecting:', this.socket?.connecting);
        
        if (this.socket?.connected) {
          // Socket is already connected, send immediately
          console.log('🔌 Socket already connected - sending agent:identify event');
          this.socket.emit('agent:identify', {
            userId: this.agentId,
            agentName: this.agentName,
            clientType: this.socketClientType()
          });
          console.log('🔌 Agent identification sent to server');
        } else {
          console.log('🔌 Socket not connected yet - agent:identify will be sent on connect');
          console.log('🔍 DEBUG: Socket will connect automatically due to autoConnect: true');
        }
        
        // Setup resilience handlers (heartbeat, beforeunload, visibilitychange)
        this.setupResilienceHandlers();
      }
      
      // Request browser permissions first
      await this.requestPermissions();
      
      const extractedIdentity = this.extractIdentityFromToken(token);
      if (extractedIdentity) {
        console.log('🆔 Twilio token identity:', extractedIdentity);
        if (!agentId) {
          this.agentId = extractedIdentity;
          this.agentName = extractedIdentity;
        }
      } else {
        console.warn('⚠️ Twilio token did not contain an identity claim');
      }

      const { Device } = await loadTwilioVoiceSdk();
      this.device = new Device(token, {
        logLevel: 'debug'
      });
      this.logDeviceRegistrationStatus('post-device-constructor');

      // Set up device event listeners
      this.setupDeviceListeners();

      await this.device.register();
      await this.waitUntilRegistered(10000);
      this.isInitialized = true;
      console.log('✅ Twilio Device registered successfully');
      this.logDeviceRegistrationStatus('post-device-register');
      
    } catch (error) {
      console.error('❌ Error initializing Twilio Device:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  // Request browser permissions for microphone and speaker
  private async requestPermissions(): Promise<void> {
    try {
      console.log('🎤 Requesting microphone permission...');
      
      const micTimeoutMs = 25_000;
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }),
        new Promise<MediaStream>((_, reject) => {
          window.setTimeout(() => {
            reject(
              new Error(
                'Microphone permission timed out. Click Allow in the browser bar, or check site permissions.',
              ),
            );
          }, micTimeoutMs);
        }),
      ]);
      
      // Stop the stream immediately as we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      console.log('✅ Microphone permission granted');
      
      // For speaker permission, we'll handle it when the call starts
      console.log('🔊 Speaker permission will be requested when call starts');
      
    } catch (error) {
      console.error('❌ Error requesting permissions:', error);
      throw new Error('Microphone permission is required for calling');
    }
  }

  // Set up device event listeners
  private setupDeviceListeners(): void {
    if (!this.device) return;

    this.device.on('registered', () => {
      console.log('📱 Device registered and ready for calls');
      this.logDeviceRegistrationStatus('event:registered');
    });

    this.device.on('ready', () => {
      console.log('📱 Device ready event fired - agent should now receive calls');
      this.logDeviceRegistrationStatus('event:ready');
    });

    this.device.on('unregistered', async () => {
      console.log('📱 Device unregistered - attempting to re-register...');
      this.logDeviceRegistrationStatus('event:unregistered');
      
      // Auto re-register if we're still online and have an agent ID
      if (this.agentId && navigator.onLine) {
        try {
          // Wait a moment before re-registering to avoid rapid retries
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Check if device still exists and is unregistered
          if (this.device && this.device.state !== 'registered') {
            console.log('📱 Re-registering Twilio Device...');
            await this.device.register();
            console.log('✅ Device re-registered successfully');
            this.logDeviceRegistrationStatus('auto-re-registered');
          }
        } catch (error) {
          console.error('❌ Failed to re-register device:', error);
          // Try to get a new token and reinitialize
          console.log('🔄 Attempting full device reinitialization...');
          try {
            const response = await fetch(`${TWILIO_API_URL}/tokens/generate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ userId: this.getCurrentUserId() })
            });
            
            if (response.ok) {
              const data = await response.json();
              await this.device?.updateToken(data.token);
              await this.device?.register();
              console.log('✅ Device reinitialized and registered');
            }
          } catch (reinitError) {
            console.error('❌ Failed to reinitialize device:', reinitError);
          }
        }
      }
    });

    this.device.on('incoming', (call: any) => {
      console.log('📞 Incoming call received:', call);
      this.handleIncomingCallFromDevice(call);
    });

    this.device.on('tokenWillExpire', async () => {
      console.log('⚠️ Token will expire soon, refreshing...');
      try {
        // Get new token from backend
        const response = await fetch(`${TWILIO_API_URL}/tokens/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ userId: this.getCurrentUserId() })
        });
        
        if (response.ok) {
          const data = await response.json();
          await this.device?.updateToken(data.token);
          console.log('✅ Token refreshed successfully');
        } else {
          console.error('❌ Failed to refresh token');
        }
      } catch (error) {
        console.error('❌ Error refreshing token:', error);
      }
    });

    this.device.on('error', (error: any) => {
      console.error('❌ Device error:', error);
    });
  }

  // Handle incoming call from Twilio Device
  private handleIncomingCallFromDevice(call: any): void {
    if (this.hangupRequested) {
      console.log('📞 [handleIncomingCallFromDevice] Ignored — hang up in progress');
      try {
        call?.reject?.();
      } catch {
        /* ignore */
      }
      return;
    }
    if (this.isActiveOutboundSession()) {
      console.log('⚠️ [handleIncomingCallFromDevice] Ignored — active outbound call in progress');
      try {
        call?.reject?.();
      } catch {
        /* ignore */
      }
      return;
    }

    console.log('📞 [handleIncomingCallFromDevice] ========== DEVICE INCOMING CALL ==========');
    console.log('📞 [handleIncomingCallFromDevice] Call parameters:', {
      From: call.parameters?.From,
      To: call.parameters?.To,
      CallerName: call.parameters?.CallerName,
      ClientId: call.parameters?.ClientId,
      ClientName: call.parameters?.ClientName,
      CallSid: call.parameters?.CallSid,
      allParams: call.parameters
    });
    
    this.call = call;
    
    // Set up call event listeners
    this.setupCallListeners();
    
    const callSid = call.parameters?.CallSid || this.callState.callSid || null;
    
    // IMPORTANT: For Device incoming calls via TaskRouter:
    // - The call comes through Twilio Device, which may have From=Twilio number
    // - The actual client phone should come from:
    //   1. Socket events (call:incoming) - most reliable
    //   2. Task attributes (need to fetch from backend)
    //   3. Or wait for socket event to update with correct clientPhone
    
    // For now, extract what we can from call parameters
    // But NOTE: From might be Twilio number, not client number!
    const fromParam = call.parameters?.From || '';
    const toParam = call.parameters?.To || '';
    const isTwilioNumber = fromParam.startsWith('+') && !fromParam.startsWith('+1') ? false : 
                          fromParam.length === 12 || fromParam.length === 11; // Heuristic: Twilio numbers are often 11-12 digits
    
    console.log('📞 [handleIncomingCallFromDevice] Phone analysis:', {
      fromParam,
      toParam,
      isTwilioNumberHeuristic: isTwilioNumber,
      warning: 'From parameter might be Twilio number, not client number!',
      recommendation: 'Wait for socket event (call:incoming) to get correct clientPhone from task attributes'
    });
    
    // Use From parameter as temporary, but socket event will override with correct clientPhone
    const tempPhone = fromParam || 'Unknown';
    const callerName = call.parameters?.CallerName || 'Caller';
    const clientId = (call.parameters?.ClientId as string | undefined) ?? null;
    const clientNameParam =
      (call.parameters?.ClientName as string | undefined) ??
      (call.parameters?.CallerName as string | undefined) ??
      null;
    
    // Check if From is likely a Twilio number
    // Twilio numbers for this account should be checked
    // For now, if To is client:agent_xxx, then From might be Twilio number
    const isToAgent = toParam?.includes('client:agent_');
    const knownTwilioNumbers = ['+14377478553', '+14374948464']; // Add your Twilio numbers
    const isLikelyTwilioNumber = knownTwilioNumbers.includes(fromParam);
    
    // IMPORTANT: Don't set clientPhone here if From looks like Twilio number
    // The socket event (call:incoming) will provide the correct clientPhone from task attributes
    // OR we'll fetch it from backend using callSid
    const shouldUseFromAsClient = !isToAgent && !isLikelyTwilioNumber;
    
    console.log('📞 [handleIncomingCallFromDevice] Phone number decision:', {
      fromParam,
      toParam,
      isToAgent,
      isLikelyTwilioNumber,
      shouldUseFromAsClient,
      clientId,
      clientName: clientNameParam,
      callSid,
      note: 'Will be updated when socket event arrives or we fetch task details'
    });
    
    // IMPORTANT: Preserve existing client info if socket event already set it
    // The socket event (call:incoming) may fire before the device event, so we should
    // preserve that data instead of overwriting it with potentially incomplete device data
    const existingClientName = this.callState.clientName;
    const existingClientId = this.callState.clientId;
    const existingClientPhone = this.callState.clientPhone;
    const existingCallerInfo = this.callState.callerInfo;
    
    // Helper to check if a name is a real client name (not a placeholder)
    const isRealClientName = (name: string | null | undefined): boolean => {
      if (!name) return false;
      const lower = name.toLowerCase();
      return lower !== 'caller' && lower !== 'unknown' && lower !== 'unknown caller';
    };
    
    // Only use device parameters if we don't already have client info from socket event
    // Prefer existing data from socket event over device parameters
    const finalClientName = isRealClientName(existingClientName)
      ? existingClientName
      : (isRealClientName(clientNameParam) ? clientNameParam : callerName);
    
    const finalClientId = existingClientId || clientId || null;
    
    // Prefer existing client phone, then device phone if it's valid
    const finalClientPhone = existingClientPhone || (shouldUseFromAsClient ? tempPhone : null);
    
    // Prefer existing callerInfo if it has real data, otherwise create from available data
    const finalCallerInfo = (existingCallerInfo && isRealClientName(existingCallerInfo.name))
      ? existingCallerInfo
      : {
          name: finalClientName,
          phone: finalClientPhone || (shouldUseFromAsClient ? tempPhone : '')
        };
    
    console.log('📞 [handleIncomingCallFromDevice] Preserving existing client info:', {
      hadExistingClientName: !!existingClientName,
      hadExistingClientId: !!existingClientId,
      hadExistingClientPhone: !!existingClientPhone,
      existingClientName,
      deviceClientName: clientNameParam,
      finalClientName,
      finalClientId,
      finalClientPhone,
      preservingSocketData: isRealClientName(existingClientName) || !!existingClientId || !!existingClientPhone
    });
    
    // Ensure the UI reflects the ringing call while we wait for the agent to answer
    // NOTE: Preserve existing client info from socket event if available
    this.updateCallState({
      status: 'ringing',
      isConnected: false,
      callSid,
      direction: 'inbound',
      callerInfo: finalCallerInfo,
      clientId: finalClientId,
      clientName: finalClientName,
      clientPhone: finalClientPhone
    });
    
    // If we can't determine client phone from Device parameters, try to fetch from backend
    if (!shouldUseFromAsClient && callSid) {
      console.log('📞 [handleIncomingCallFromDevice] Fetching task details from backend for callSid:', callSid);
      this.fetchTaskDetailsForCall(callSid).catch(error => {
        console.warn('⚠️ [handleIncomingCallFromDevice] Failed to fetch task details:', error);
      });
    }
    
    console.log('📞 [handleIncomingCallFromDevice] Call state updated - waiting for socket event or task fetch with correct clientPhone');
    console.log('📞 [handleIncomingCallFromDevice] ===========================================');
    
    // Start the incoming ringtone if it's not already playing
    // Commented out - using Twilio's default incoming sound
    // this.playIncomingSound();

    if (this.shouldAutoAcceptIncomingCall()) {
      console.log('🤖 Auto-accept enabled. Accepting incoming call automatically.');
      setTimeout(async () => {
        try {
          await this.acceptIncomingCall();
          console.log('🤖 Auto-accept completed successfully.');
        } catch (error) {
          console.error('❌ Auto-accept failed:', error);
        }
      }, 250);
    }
  }

  // Handle RINGING STATE - Agent gets incoming call notification
  private handleRingingState(data: any): void {
    if (this.hangupRequested || Date.now() < this.hangupUiLockUntil) {
      console.log('📞 [handleRingingState] Ignored — hang up in progress');
      return;
    }
    console.log('🔍 [handleRingingState] Starting to process incoming call');
    console.log('🔍 [handleRingingState] Full data received:', JSON.stringify(data, null, 2));

    // New ring — reset the local-accept marker.
    this.acceptedLocally = false;

    // Store the incoming call data for later use
    (this as any).incomingCallData = data;
    console.log('🔍 [handleRingingState] Stored incomingCallData');
    
    // Update call state to RINGING
    // Extract client phone - prefer clientPhone from data, fallback to callerPhone
    // For inbound calls, callerPhone should be the client's number, but we prefer explicit clientPhone
    const clientPhone = data.clientPhone ?? data.ClientPhone ?? data.callerPhone ?? null;
    
    console.log('🔍 [handleRingingState] Phone number extraction:', {
      dataClientPhone: data.clientPhone,
      dataClientPhoneUpper: data.ClientPhone,
      dataCallerPhone: data.callerPhone,
      extractedClientPhone: clientPhone,
      fallbackUsed: !clientPhone && data.callerPhone ? 'using callerPhone' : 'using clientPhone or null'
    });
    
    // Extract channel from data - default to 'direct' if not provided
    const channel = (data.channel ?? data.Channel ?? 'direct') as 'direct' | 'whatsapp' | 'app';
    
    this.updateCallState({
      status: 'ringing',
      direction: 'inbound',
      callerInfo: {
        name: data.callerName || 'Caller',
        phone: clientPhone || data.callerPhone || 'Unknown'
      },
      callSid: data.callSid || null,
      queueTime: 0,
      clientId: data.clientId ?? data.ClientId ?? this.callState.clientId ?? null,
      clientName: data.clientName ?? data.ClientName ?? data.callerName ?? this.callState.clientName ?? null,
      clientPhone: clientPhone ?? this.callState.clientPhone ?? null,
      channel: channel
    });
    
    if (isAgentPhoneWindow()) {
      publishScreenPop({
        clientId: this.callState.clientId,
        clientName: this.callState.clientName,
        clientPhone: this.callState.clientPhone,
        callSid: this.callState.callSid,
      });
    }

    console.log('🔍 [handleRingingState] Updated call state to ringing');
    console.log('🔍 [handleRingingState] Final call state:', {
      status: this.callState.status,
      clientPhone: this.callState.clientPhone,
      callerInfoPhone: this.callState.callerInfo?.phone,
      clientId: this.callState.clientId,
      clientName: this.callState.clientName,
      direction: this.callState.direction
    });
    
    // Play incoming call sound
    // Commented out - using Twilio's default incoming sound
    // this.playIncomingSound();

    // Set timeout for incoming call (30 seconds)
    this.incomingCallTimeout = setTimeout(() => {
      console.log('⏰ RINGING STATE: Incoming call timeout - stopping ringing and clearing call state');
      this.stopRingingMusic();
      this.clearRingingState();
    }, 30000); // 30 seconds timeout
  }


  /** Track ring-client id so decline/cancel events from other attempts are ignored. */
  setOutboundAppRingCallId(callId: string | null): void {
    this.outboundAppRingCallId = callId;
  }

  private markOutboundPlacementCancelled(): void {
    this.cancelledOutboundPlaceTokens.add(this.outboundPlaceToken);
  }

  isOutboundPlacementCancelled(placeToken: number): boolean {
    return this.cancelledOutboundPlaceTokens.has(placeToken);
  }

  /**
   * Wait during ring-client prep; returns false if the agent hung up or placement was cancelled.
   */
  async waitForOutboundPlaceOrCancel(placeToken: number, ms: number): Promise<boolean> {
    const started = Date.now();
    while (Date.now() - started < ms) {
      if (this.isOutboundPlacementCancelled(placeToken)) {
        return false;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return !this.isOutboundPlacementCancelled(placeToken);
  }

  private matchesOutboundAppRing(data?: { callId?: string } | null): boolean {
    if (this.callState.channel !== 'app' || this.callState.direction !== 'outbound') {
      return false;
    }
    if (!data?.callId) {
      return true;
    }
    if (!this.outboundAppRingCallId) {
      return data.callId.startsWith(`app-voice:${this.callState.clientId ?? ''}:`);
    }
    return data.callId === this.outboundAppRingCallId;
  }

  private shouldNotifyAppCancelRing(): boolean {
    // Client already tapped Accept — cancel-ring would terminate the Twilio leg still
    // in "ringing" (answerOnBridge) and the phone shows "Call ended" immediately.
    if (this.outboundAppClientAnswered) {
      return false;
    }
    if (this.callState.channel === 'app' && this.call) {
      return false;
    }
    return (
      this.callState.channel === 'app' &&
      !!this.callState.clientId &&
      (this.isOutboundDialPrep() ||
        (this.callState.direction === 'outbound' &&
          this.callState.status === 'ringing' &&
          !this.callState.isConnected))
    );
  }

  /** Mobile client accepted — keep outbound UI until admin Twilio leg bridges. */
  private handleOutboundAppClientAnswered(data: any): void {
    if (!this.matchesOutboundAppRing(data)) {
      console.log('📞 [Outbound app] Ignoring call:accepted for unrelated callId:', data?.callId);
      return;
    }

    this.clearOutboundEndTimer();
    this.stopRingingMusic();

    if (typeof data?.callId === 'string') {
      this.outboundAppRingCallId = data.callId;
    }
    this.outboundAppClientAnswered = true;

    const clientPhone =
      data.clientPhone ?? data.ClientPhone ?? data.callerPhone ?? this.callState.clientPhone ?? null;

    this.updateCallState({
      status: 'ringing',
      isConnected: false,
      direction: 'outbound',
      callerInfo: {
        name: data.callerName || this.callState.clientName || 'Client',
        phone: clientPhone || this.callState.callerInfo?.phone || '',
      },
      clientId: data.clientId ?? data.ClientId ?? this.callState.clientId ?? null,
      clientName: data.clientName ?? data.ClientName ?? data.callerName ?? this.callState.clientName ?? null,
      clientPhone: clientPhone ?? this.callState.clientPhone ?? null,
      channel: 'app',
    });

    console.log('📞 [Outbound app] Client accepted — waiting for admin Twilio bridge');
  }

  // Handle IN-CALL STATE - Agent accepted the call
  private handleInCallState(data: any): void {
    if (this.hangupRequested || Date.now() < this.hangupUiLockUntil) {
      console.log('📞 [handleInCallState] Ignored — hang up in progress');
      return;
    }
    console.log('🔍 [handleInCallState] Starting to process call accepted');
    console.log('🔍 [handleInCallState] Full data received:', JSON.stringify(data, null, 2));
    
    // Clear incoming call data since call is now accepted
    (this as any).incomingCallData = null;
    
    // CRITICAL: Clear the incoming call timeout to prevent banner from disappearing
    if (this.incomingCallTimeout) {
      console.log('🔍 [handleInCallState] Clearing incoming call timeout');
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }
    
    // Stop ringing music immediately
    this.stopRingingMusic();
    
    // Extract client phone - prefer clientPhone from data, fallback to callerPhone
    const clientPhone = data.clientPhone ?? data.ClientPhone ?? data.callerPhone ?? null;
    
    console.log('🔍 [handleInCallState] Phone number extraction:', {
      dataClientPhone: data.clientPhone,
      dataClientPhoneUpper: data.ClientPhone,
      dataCallerPhone: data.callerPhone,
      extractedClientPhone: clientPhone,
      currentStateClientPhone: this.callState.clientPhone,
      finalClientPhone: clientPhone ?? this.callState.clientPhone ?? null
    });
    
    // Extract channel from data - preserve from ringing state or default to 'direct'
    const channel = (data.channel ?? data.Channel ?? this.callState.channel ?? 'direct') as 'direct' | 'whatsapp' | 'app';
    
    // Update call state to IN-CALL
    this.updateCallState({
      status: 'in-call',
      isConnected: true,
      direction: 'inbound',
      callerInfo: {
        name: data.callerName || 'Caller',
        phone: clientPhone || data.callerPhone || 'Unknown'
      },
      callSid: data.callSid || null,
      queueTime: data.queueTime || 0,
      clientId: data.clientId ?? data.ClientId ?? this.callState.clientId ?? null,
      clientName: data.clientName ?? data.ClientName ?? data.callerName ?? this.callState.clientName ?? null,
      clientPhone: clientPhone ?? this.callState.clientPhone ?? null,
      channel: channel
    });
    
    console.log('🔍 [handleInCallState] Updated call state to in-call');
    console.log('🔍 [handleInCallState] Final call state:', {
      status: this.callState.status,
      clientPhone: this.callState.clientPhone,
      callerInfoPhone: this.callState.callerInfo?.phone,
      clientId: this.callState.clientId,
      clientName: this.callState.clientName,
      direction: this.callState.direction
    });
    
    // Start call timer
    this.startCallTimer(data.queueTime || 0);
  }

  private shouldAutoAcceptIncomingCall(): boolean {
    try {
      const localOverride = window.localStorage.getItem('twilioAutoAccept');
      if (localOverride !== null) {
        return localOverride.toLowerCase() === 'true';
      }
    } catch (error) {
      console.warn('⚠️ Unable to read twilioAutoAccept override from localStorage:', error);
    }

    return `${WebRTCCallService.AUTO_ACCEPT_ENV}`.toLowerCase() === 'true';
  }

  private matchesActiveCallSid(...sids: Array<string | null | undefined>): boolean {
    const activeSid = this.callState.callSid;
    if (!activeSid) {
      return true;
    }
    // PSTN/conference legs use different SIDs on parent vs child — still the same conversation.
    if (this.callState.status === 'in-call') {
      return sids.some((sid) => typeof sid === 'string' && sid.length > 0);
    }
    return sids.some((sid) => sid && (sid === activeSid || activeSid.startsWith(sid) || sid.startsWith(activeSid)));
  }

  /** Twilio Call leg ended (disconnect/cancel/error) — clear UI unless agent is hanging up. */
  private shouldClearOnCallLegEnded(): boolean {
    if (this.hangupRequested) {
      return false;
    }
    if (this.isEstablishedCall() || this.callState.status === 'in-call') {
      return true;
    }
    if (this.callState.direction === 'outbound' && this.call) {
      return true;
    }
    return this.callState.direction === 'inbound';
  }

  /** Remote party hung up or call leg completed — tear down banner and Twilio state. */
  private clearCallBecauseRemoteEnded(reason: string, data?: Record<string, unknown>): void {
    if (this.hangupRequested && Date.now() < this.hangupUiLockUntil) {
      console.log(`📞 ${reason}: ignored — agent hang up in progress`);
      return;
    }

    if (this.callState.status === 'idle') {
      return;
    }

    if (this.isOutboundDialPrep() && this.callState.channel === 'app' && !this.outboundAppClientAnswered) {
      console.log(`📞 ${reason}: ignored during app outbound dial prep`);
      return;
    }

    const endedSid = typeof data?.callSid === 'string' ? data.callSid : null;
    const dialCallSid = typeof data?.dialCallSid === 'string' ? data.dialCallSid : null;
    const agentCallSid = typeof data?.agentCallSid === 'string' ? data.agentCallSid : null;

    if (
      endedSid &&
      this.callState.callSid &&
      !this.matchesActiveCallSid(endedSid, dialCallSid, agentCallSid)
    ) {
      console.log(`📞 ${reason}: ignored — unrelated callSid`, { endedSid, activeSid: this.callState.callSid });
      return;
    }

    console.log(`📞 ${reason}: clearing call UI`);

    // Reject the Twilio call object so the browser device stops ringing immediately.
    // Without this, the Twilio Voice SDK browser device keeps playing its ring tone
    // even after we clear the JS call state.
    if (this.call) {
      try {
        console.log(`📞 ${reason}: rejecting Twilio call to stop ringing sound`);
        this.call.reject();
      } catch (err) {
        console.warn(`📞 ${reason}: error rejecting Twilio call`, err);
      }
    }

    this.stopRingingMusic();
    this.endCall({ notifyAppCancel: this.callState.channel === 'app' });
  }

  // Handle call ended from socket
  private handleCallEnded(data: any): void {
    const endedSid = typeof data?.callSid === 'string' ? data.callSid : null;

    if (this.isEstablishedCall() || this.callState.status === 'in-call') {
      if (
        endedSid &&
        this.callState.callSid &&
        !this.matchesActiveCallSid(endedSid, data?.dialCallSid, data?.agentCallSid)
      ) {
        console.log('📞 Ignoring call:ended for unrelated leg during active call:', endedSid);
        return;
      }
      this.clearCallBecauseRemoteEnded('call:ended (active session)', data);
      return;
    }

    // While ringing before device.connect, ignore stale call:ended — otherwise connect never runs.
    if (this.isOutboundDialPrep() && this.callState.channel === 'app') {
      console.log('📞 Ignoring call:ended during app outbound dial prep (before Twilio connect)');
      return;
    }

    if (
      this.callState.channel === 'app' &&
      this.callState.direction === 'outbound' &&
      this.callState.status === 'ringing' &&
      !this.callState.isConnected
    ) {
      if (this.call) {
        console.log('📞 Ignoring call:ended during outbound connect (Twilio leg active)');
        return;
      }
      if (this.outboundAppClientAnswered) {
        console.log('📞 call:ended during outbound app bridge — not auto-cutting (wait for connect/disconnect)');
        return;
      }
      console.log('📞 call:ended during outbound ring — not auto-cutting (use Hang Up to cancel)');
      return;
    }

    if (
      endedSid &&
      this.callState.callSid &&
      endedSid !== this.callState.callSid
    ) {
      console.log('📞 Ignoring call:ended for unrelated callSid:', endedSid);
      return;
    }

    if (
      this.callState.direction === 'outbound' &&
      this.callState.status === 'ringing' &&
      !this.callState.isConnected &&
      !this.call
    ) {
      console.log('📞 call:ended during outbound ring — not auto-cutting (use Hang Up to cancel)');
      return;
    }

    if (
      this.callState.direction === 'outbound' &&
      this.callState.status === 'ringing' &&
      this.callState.isConnected
    ) {
      this.clearCallBecauseRemoteEnded('call:ended (outbound connected)', data);
      return;
    }

    if (this.callState.status === 'idle') {
      return;
    }

    this.clearCallBecauseRemoteEnded('call:ended', data);
  }


  // Handle client hangup - clears both ringing and in-call states
  private handleClientHangup(data: any): void {
    if (this.isOutboundDialPrep()) {
      if (this.callState.channel === 'app') {
        console.log('📞 CLIENT HANGUP: Ignoring during app outbound dial prep');
        return;
      }
      this.clearCallBecauseRemoteEnded('call:client-hangup (dial prep)', data);
      return;
    }

    if (
      this.callState.channel === 'app' &&
      this.callState.direction === 'outbound' &&
      !this.callState.isConnected &&
      !this.outboundAppClientAnswered &&
      !this.call
    ) {
      console.log('📞 CLIENT HANGUP: Ignoring during outbound app ring (not bridged yet)');
      return;
    }

    this.clearCallBecauseRemoteEnded('call:client-hangup', data);
  }

  private clearOutboundEndTimer(): void {
    if (this.outboundEndTimer) {
      clearTimeout(this.outboundEndTimer);
      this.outboundEndTimer = null;
    }
  }

  /** Outbound app/PSTN dial in progress — do not let queue/inbound events steal the UI. */
  private isActiveOutboundSession(): boolean {
    return (
      this.callState.direction === 'outbound' &&
      (this.callState.status === 'ringing' || this.callState.status === 'in-call')
    );
  }

  /**
   * Optional delayed cleanup for failed outbound connect only.
   * Does not auto-cut established or bridging calls — agent must use Hang Up.
   */
  private scheduleOutboundEndAfterChurn(reason: string, graceMs?: number): void {
    if (this.hangupRequested || this.isEstablishedCall()) {
      return;
    }

    if (this.callState.direction !== 'outbound') {
      return;
    }

    // Never auto-end while a Twilio call object exists (ringing or bridging).
    if (this.call) {
      console.log(`📞 [Outbound] Skipping churn cleanup — active Twilio leg (${reason})`);
      return;
    }

    if (this.outboundAppClientAnswered) {
      console.log(`📞 [Outbound] Skipping churn cleanup — client answered (${reason})`);
      return;
    }

    const delay = graceMs ?? 30_000;

    this.clearOutboundEndTimer();

    console.log(`📞 [Outbound] Scheduling optional cleanup (${reason}) in ${delay}ms`);

    this.outboundEndTimer = setTimeout(() => {
      this.outboundEndTimer = null;
      if (this.hangupRequested || this.isEstablishedCall() || this.call) {
        return;
      }
      if (this.callState.direction !== 'outbound' || this.callState.status === 'idle') {
        return;
      }
      if (!this.isOutboundDialPrep()) {
        return;
      }
      console.log(`📞 [Outbound] Clearing stuck dial prep after timeout (${reason})`);
      this.endCall({ notifyAppCancel: false });
    }, delay);
  }

  private detachCallListeners(): void {
    if (!this.call) return;
    const events = ['accept', 'disconnect', 'cancel', 'reject', 'error'] as const;
    for (const event of events) {
      try {
        if (typeof this.call.removeAllListeners === 'function') {
          this.call.removeAllListeners(event);
        }
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Clear banner/UI when no Twilio leg is active (e.g. after failed hangup or churn timer).
   */
  /** Public so ClientChats can recover when a prior app call left UI stuck on ringing. */
  reclaimStaleCallStateIfNeeded(): void {
    if (this.callState.status === 'idle') {
      if (this.device?.isBusy) {
        console.warn('📞 Device busy with idle UI — releasing orphan Twilio leg');
        void this.flushTwilioDeviceIdle(WebRTCCallService.DEVICE_IDLE_WAIT_MS);
      }
      return;
    }

    // UI still shows in-call but Twilio leg is gone (client hung up, disconnect missed).
    if (this.callState.status === 'in-call' && !this.call && !this.device?.isBusy) {
      console.warn('📞 Clearing stale in-call UI — no active Twilio leg');
      this.endCall({
        notifyAppCancel:
          this.callState.channel === 'app' && !this.outboundAppClientAnswered,
      });
      return;
    }

    // Active Twilio leg — do not auto-clear from heuristics.
    if (this.call || this.device?.isBusy) {
      return;
    }

    // Outbound prep stuck (ring-client / connect) with no Twilio leg for too long.
    if (this.isOutboundDialPrep() && this.outboundPrepStartedAt != null) {
      const prepAge = Date.now() - this.outboundPrepStartedAt;
      if (prepAge > WebRTCCallService.OUTBOUND_PREP_MAX_MS) {
        console.warn('📞 Clearing stale outbound dial prep', { prepAgeMs: prepAge });
        void this.cancelOutboundCallPrep();
        return;
      }
      return;
    }

    if (
      this.callState.direction === 'outbound' &&
      this.callState.status === 'ringing' &&
      !this.callState.isConnected &&
      this.call
    ) {
      return;
    }

    const deviceBusy = this.device?.isBusy === true;
    const hasLocalCall = !!this.call;

    if (!deviceBusy && !hasLocalCall) {
      this.clearOutboundEndTimer();
      console.warn('📞 Clearing stale call UI — no active Twilio leg');
      this.endCall({
        notifyAppCancel:
          this.callState.channel === 'app' && !this.outboundAppClientAnswered,
      });
    }
  }

  /**
   * Tear down orphan Twilio legs and wait until Device.connect() is allowed again.
   */
  private async flushTwilioDeviceIdle(maxWaitMs?: number): Promise<boolean> {
    const budget = maxWaitMs ?? WebRTCCallService.DEVICE_IDLE_WAIT_MS;
    await this.releaseTwilioDeviceCalls({
      rejectInboundRinging: true,
      maxWaitMs: budget,
    });
    if (!this.device?.isBusy) {
      return true;
    }
    return this.waitForTwilioDeviceIdle(budget);
  }

  /**
   * Last-resort recovery if the Twilio JS SDK never clears device.isBusy after disconnects.
   * We destroy the device and force a re-initialize before the next call.
   */
  private hardResetTwilioDevice(reason: string): void {
    console.warn(`⚠️ Hard resetting Twilio Device (${reason})`);
    try {
      this.detachCallListeners();
    } catch {
      /* ignore */
    }
    this.call = null;
    if (this.device) {
      try {
        // Best effort to end any remaining legs before destroy.
        this.device.disconnectAll();
      } catch {
        /* ignore */
      }
      try {
        this.device.destroy();
      } catch (error) {
        console.warn('⚠️ device.destroy failed during hard reset:', error);
      }
    }
    this.device = null;
    // Force re-init path (initialize() will reconstruct & register).
    this.isInitialized = false;
  }

  /**
   * Before a new app outbound call: end stale UI, terminate prior Twilio legs to the client,
   * and wait until Device.connect() will not throw "A Call is already active".
   */
  async prepareForAppOutboundCall(clientId: string): Promise<void> {
    this.clearOutboundEndTimer();
    this.outboundAppClientAnswered = false;
    this.reclaimStaleCallStateIfNeeded();

    const prepAge = this.outboundPrepStartedAt ? Date.now() - this.outboundPrepStartedAt : 0;
    const isRecentlyStarted = this.outboundPrepStartedAt != null && prepAge < 10000;

    if (this.isOutboundDialPrep() && !isRecentlyStarted) {
      console.warn('📞 Replacing in-flight outbound prep before new app call');
      await this.cancelOutboundCallPrep();
    }

    if (
      this.callState.status !== 'idle' &&
      this.callState.direction === 'outbound' &&
      !isRecentlyStarted &&
      (this.isOutboundDialPrep() ||
        (this.callState.status === 'ringing' && !this.callState.isConnected))
    ) {
      console.warn('📞 Clearing stale outbound session before new app call');
      await this.cancelOutboundCallPrep();
      this.endCall({ notifyAppCancel: false });
    }

    const isEstablishedOrBusy = this.callState.status !== 'idle' && !isRecentlyStarted;
    if (isEstablishedOrBusy) {
      throw new Error('Finish the current call before starting a new one.');
    }

    await this.cancelAppRingForClient(clientId);

    let idle = await this.flushTwilioDeviceIdle(
      WebRTCCallService.DEVICE_IDLE_WAIT_MS * 2,
    );

    if (!idle && this.device) {
      console.warn('📞 Twilio device still busy — forcing disconnectAll recovery');
      try {
        this.detachCallListeners();
        this.call = null;
        this.device.disconnectAll();
      } catch (error) {
        console.warn('⚠️ Forced disconnectAll failed:', error);
      }
      await new Promise((resolve) => setTimeout(resolve, 400));
      idle = await this.waitForTwilioDeviceIdle(8000);
    }

    if (!idle && this.device?.isBusy) {
      // If we are still stuck busy, hard reset so the next initialize() can recover.
      this.hardResetTwilioDevice('prepareForAppOutboundCall still busy');
      throw new Error(
        'Voice line is still busy from a previous call. Hang up the active call banner, wait a moment, and try again.',
      );
    }
  }

  /** Wait until Twilio Device._activeCall is cleared so device.connect() can run again. */
  private async waitForTwilioDeviceIdle(maxWaitMs: number): Promise<boolean> {
    if (!this.device) {
      return true;
    }
    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      if (!this.device.isBusy) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return !this.device.isBusy;
  }

  /** Wait until Twilio Device._activeCall is cleared so device.connect() can run again. */
  private async releaseTwilioDeviceCalls(
    options?: { rejectInboundRinging?: boolean; maxWaitMs?: number },
  ): Promise<void> {
    const maxWaitMs = options?.maxWaitMs ?? 5000;
    const activeCall = this.call;

    if (activeCall) {
      try {
        if (options?.rejectInboundRinging && typeof activeCall.reject === 'function') {
          await activeCall.reject();
        } else if (typeof activeCall.disconnect === 'function') {
          await activeCall.disconnect();
        }
      } catch (error) {
        console.warn('⚠️ Failed to end local Twilio call leg:', error);
      }
    }

    this.call = null;

    if (!this.device) {
      return;
    }

    const disconnectEveryLeg = (): void => {
      try {
        const legs = typeof this.device?.calls !== 'undefined' ? [...this.device.calls] : [];
        for (const leg of legs) {
          try {
            if (options?.rejectInboundRinging && typeof leg.reject === 'function') {
              leg.reject();
            } else if (typeof leg.disconnect === 'function') {
              leg.disconnect();
            }
          } catch {
            /* ignore per-leg */
          }
        }
      } catch {
        /* ignore */
      }
      try {
        this.device?.disconnectAll();
      } catch (error) {
        console.warn('⚠️ device.disconnectAll failed:', error);
      }
    };

    disconnectEveryLeg();

    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      if (!this.device.isBusy) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    if (this.device.isBusy) {
      console.warn('⚠️ Twilio device still busy after release — final disconnectAll');
      disconnectEveryLeg();
      // If the SDK remains stuck busy even after a second flush, reset the Device.
      // This prevents the app from getting wedged where all future connect() calls throw.
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (this.device?.isBusy) {
        this.hardResetTwilioDevice('releaseTwilioDeviceCalls still busy');
      }
    }
  }

  private async cancelAppRingForClient(
    clientId: string,
    callId?: string | null,
  ): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/app-voice/cancel-ring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          clientId,
          ...(callId ? { callId } : {}),
        }),
      });
      if (!response.ok) {
        console.warn('⚠️ cancel-ring failed:', response.status);
      }
    } catch (error) {
      console.warn('⚠️ Failed to cancel app ring on client:', error);
    }
  }

  // Set up call event listeners
  private setupCallListeners(): void {
    if (!this.call) return;

    this.call.on('accept', async (connection: any) => {
      if (this.hangupRequested) {
        console.log('📞 Ignoring accept — hang up already requested');
        return;
      }
      console.log('✅ Call accepted');
      this.acceptedLocally = true;
      // Commented out - using Twilio's default incoming sound
      // this.stopIncomingSound();

      // CRITICAL: Clear the incoming call timeout to prevent banner from disappearing
      if (this.incomingCallTimeout) {
        console.log('✅ [Call.accept] Clearing incoming call timeout');
        clearTimeout(this.incomingCallTimeout);
        this.incomingCallTimeout = null;
      }

      const callSid =
        connection?.parameters?.CallSid ??
        this.call?.parameters?.CallSid ??
        this.callState.callSid ??
        null;

      this.updateCallState({
        status: 'in-call',
        isConnected: true,
        callSid
      });
      this.clearOutboundEndTimer();

      try {
        await this.requestSpeakerPermission();
      } catch (error) {
        console.warn('⚠️ Could not request speaker permission after call acceptance:', error);
      }

      this.startCallTimer(0); // Start timer from call acceptance
    });

    this.call.on('disconnect', () => {
      console.log('📞 Call disconnected');
      if (!this.shouldClearOnCallLegEnded()) {
        return;
      }
      this.endCall({ notifyAppCancel: this.callState.channel === 'app' });
    });

    this.call.on('cancel', () => {
      console.log('📞 Call cancelled');
      if (!this.shouldClearOnCallLegEnded()) {
        return;
      }
      this.endCall({ notifyAppCancel: this.callState.channel === 'app' });
    });

    this.call.on('reject', () => {
      console.log('📞 Call rejected');
      if (!this.shouldClearOnCallLegEnded()) {
        return;
      }
      this.endCall({ notifyAppCancel: this.callState.channel === 'app' });
    });

    this.call.on('error', (error: any) => {
      console.error('❌ Call error:', error);
      if (!this.shouldClearOnCallLegEnded()) {
        return;
      }
      this.endCall({ notifyAppCancel: this.callState.channel === 'app' });
    });
  }

  private isOutboundDialPrep(): boolean {
    return (
      this.callState.status === 'ringing' &&
      this.callState.direction === 'outbound' &&
      !this.call
    );
  }

  /** Active conversation — do not auto-cut from stale socket/Twilio churn. */
  private isEstablishedCall(): boolean {
    return (
      this.callState.status === 'in-call' &&
      this.callState.isConnected &&
      !!this.call
    );
  }

  /** Show outbound dialing UI before ring-client / device.connect completes. */
  beginOutboundCallSession(options: {
    clientName?: string;
    clientId?: string;
    clientPhone?: string;
    channel?: CallState['channel'];
  }): number {
    this.reclaimStaleCallStateIfNeeded();

    if (this.callState.status !== 'idle') {
      throw new Error('You are already handling a call');
    }

    const placeToken = ++this.outboundPlaceToken;
    this.outboundPrepStartedAt = Date.now();
    this.outboundAppClientAnswered = false;

    const displayPhone = options.clientPhone ?? '';
    this.updateCallState({
      status: 'ringing',
      isConnected: false,
      direction: 'outbound',
      callerInfo: {
        name: options.clientName || 'Client',
        phone: displayPhone,
      },
      callSid: null,
      callDuration: 0,
      queueTime: 0,
      clientId: options.clientId ?? null,
      clientName: options.clientName ?? 'Client',
      clientPhone: displayPhone,
      channel: options.channel ?? 'direct',
    });

    return placeToken;
  }

  /** Cancel outbound UI when ring-client or connect fails before a Twilio leg exists. */
  cancelOutboundCallPrep(): void {
    if (this.outboundAppClientAnswered) {
      console.log(
        '📞 [Outbound app] Skipping cancelOutboundCallPrep — client already answered',
      );
      return;
    }
    this.markOutboundPlacementCancelled();
    if (this.isOutboundDialPrep()) {
      void this.cancelAppRingForClient(
        this.callState.clientId ?? '',
        this.outboundAppRingCallId,
      );
      this.endCall({ notifyAppCancel: true });
    }
  }

  async initiateOutboundCall(options: {
    phoneNumber: string;
    clientName?: string;
    clientId?: string;
    agentId?: string;
    channel?: CallState['channel'];
    placeToken?: number;
  }): Promise<void> {
    if (
      options.placeToken != null &&
      this.isOutboundPlacementCancelled(options.placeToken)
    ) {
      throw new Error('Call was cancelled before it could connect');
    }

    if (!this.device) {
      throw new Error('Voice device is not initialized');
    }

    if (this.device.state !== 'registered') {
      await this.ensureDeviceRegistered();
    }

    const deviceBusy = this.device.isBusy || !!this.call;
    if (deviceBusy) {
      await this.releaseTwilioDeviceCalls({
        maxWaitMs: WebRTCCallService.DEVICE_IDLE_WAIT_MS,
      });
    }

    if (
      options.placeToken != null &&
      this.isOutboundPlacementCancelled(options.placeToken)
    ) {
      throw new Error('Call was cancelled before it could connect');
    }

    if (!this.device?.isBusy) {
      /* already idle */
    } else {
      await this.waitForTwilioDeviceIdle(WebRTCCallService.DEVICE_IDLE_WAIT_MS);
    }

    if (this.callState.status !== 'idle' && !this.isOutboundDialPrep()) {
      throw new Error('You are already handling a call');
    }

    const normalizedNumber = this.normalizeOutboundNumber(options.phoneNumber);
    if (!normalizedNumber) {
      throw new Error('Invalid destination number');
    }

    const isAppClientCall =
      options.channel === 'app' || normalizedNumber.toLowerCase().includes('app_client_');
    const outboundChannel: CallState['channel'] = isAppClientCall
      ? 'app'
      : (options.channel ?? 'direct');

    if (!this.isOutboundDialPrep()) {
      this.updateCallState({
        status: 'ringing',
        isConnected: false,
        direction: 'outbound',
        callerInfo: {
          name: options.clientName || 'Client',
          phone: normalizedNumber,
        },
        callSid: null,
        callDuration: 0,
        queueTime: 0,
        clientId: options.clientId ?? null,
        clientName: options.clientName ?? 'Client',
        clientPhone: normalizedNumber,
        channel: outboundChannel,
      });
    } else {
      this.updateCallState({
        clientId: options.clientId ?? null,
        clientName: options.clientName ?? 'Client',
        clientPhone: normalizedNumber,
        channel: outboundChannel,
        callerInfo: {
          name: options.clientName || 'Client',
          phone: normalizedNumber,
        },
      });
    }

    try {
      const connectParams: Record<string, string> = {
        To: normalizedNumber,
        ClientId: options.clientId ?? '',
        AgentId: options.agentId ?? '',
        Direction: 'outbound',
      };

      connectParams.Channel = outboundChannel;
      connectParams.channel = outboundChannel;
      if (isAppClientCall && options.clientName) {
        connectParams.CallerName = options.clientName;
      }

      const connectOnce = async (): Promise<any> => {
        const connection = this.device!.connect({
          params: connectParams,
        });
        let establishedCall: any = connection;
        if (connection && typeof (connection as Promise<any>).then === 'function') {
          console.log('🔍 initiateOutboundCall: Device.connect returned a promise, awaiting call instance...');
          establishedCall = await (connection as Promise<any>);
        }
        return establishedCall;
      };

      if (
        options.placeToken != null &&
        this.isOutboundPlacementCancelled(options.placeToken)
      ) {
        throw new Error('Call was cancelled before it could connect');
      }

      const CONNECT_TIMEOUT_MS = 20_000;
      let establishedCall: any;
      try {
        establishedCall = await Promise.race([
          connectOnce(),
          new Promise<never>((_, reject) => {
            setTimeout(
              () => reject(new Error('Call connection timed out. Check that you are Available and try again.')),
              CONNECT_TIMEOUT_MS,
            );
          }),
        ]);
      } catch (connectError: unknown) {
        const message =
          connectError instanceof Error ? connectError.message : String(connectError);
        if (message.toLowerCase().includes('call is already active')) {
          console.warn('📞 Twilio reported active call — releasing and retrying connect');
          await this.releaseTwilioDeviceCalls({
            maxWaitMs: WebRTCCallService.DEVICE_IDLE_WAIT_MS,
          });
          const idle = await this.waitForTwilioDeviceIdle(
            WebRTCCallService.DEVICE_IDLE_WAIT_MS,
          );
          if (!idle) {
            throw new Error(
              'Voice line is still busy from a previous call. Hang up the call banner and try again.',
            );
          }
          establishedCall = await connectOnce();
        } else {
          throw connectError;
        }
      }

      if (!establishedCall) {
        throw new Error('Failed to initiate outbound call');
      }

      this.call = establishedCall;
      console.log('✅ initiateOutboundCall: Call instance acquired', {
        hasOn: typeof this.call.on === 'function',
        parameters: this.call.parameters
      });

      const outboundSid = establishedCall?.parameters?.CallSid;
      if (outboundSid) {
        this.updateCallState({ callSid: outboundSid });
      }

      this.setupCallListeners();
    } catch (error: unknown) {
      console.error('❌ Error starting outbound call:', error);
      this.endCall();
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              typeof (error as { message?: string }).message === 'string'
            ? (error as { message: string }).message
            : 'Failed to start outbound call';
      throw new Error(message);
    }
  }

  // Accept an incoming call when the agent confirms in the UI
  async acceptIncomingCall(): Promise<void> {
    if (this.callState.status === 'in-call' && this.callState.isConnected) {
      console.log('📞 acceptIncomingCall: Call already connected, skipping manual accept.');
      return;
    }

    if (!this.call) {
      throw new Error('No incoming call available to accept.');
    }

    // CRITICAL: Clear the incoming call timeout immediately when accepting
    if (this.incomingCallTimeout) {
      console.log('📞 acceptIncomingCall: Clearing incoming call timeout');
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }

    console.log('📞 acceptIncomingCall: Sending accept to Twilio Device...');

    // Mark before awaiting: reservation.accepted can race back over the socket
    // before this promise resolves, and we must recognise it as a local accept.
    this.acceptedLocally = true;

    const callSid =
      this.call?.parameters?.CallSid ??
      this.callState.callSid ??
      null;
    const taskSid =
      (typeof this.call?.customParameters?.get === "function"
        ? (this.call.customParameters.get("TaskSid"))
        : null) ?? null;

    if (this.socket && this.socket.connected) {
      console.log("📡 [WebRTCCall] Emitting call:accepted-from-device (web):", { callSid, taskSid, agentId: this.agentId });
      this.socket.emit("call:accepted-from-device", {
        device: "web",
        callSid,
        taskSid,
        agentId: this.agentId,
      });
    }

    try {
      await this.call.accept();
      console.log('✅ acceptIncomingCall: Twilio Device accept completed.');
    } catch (error) {
      console.error('❌ acceptIncomingCall: Failed to accept call:', error);
      throw error;
    }
  }


  // Request speaker permission
  private async requestSpeakerPermission(): Promise<void> {
    try {
      // For speaker permission, we need to play audio to request permission
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create a silent audio buffer to trigger speaker permission
      const buffer = audioContext.createBuffer(1, 1, 22050);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start();
      
      console.log('🔊 Speaker permission requested');
    } catch (error) {
      console.warn('⚠️ Could not request speaker permission:', error);
    }
  }

  // Toggle mute
  async holdCall(): Promise<void> {
    try {
      const callSid = this.callState.callSid;
      if (!callSid) {
        throw new Error('No active call to hold');
      }

      if (this.callState.isOnHold) {
        console.log('📞 Call is already on hold');
        return;
      }

      console.log('⏸️ [HoldCall] Putting call on hold...', { callSid });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${TWILIO_API_URL}/calls/${callSid}/hold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to hold call');
      }

      const result = await response.json();
      console.log('✅ [HoldCall] Call placed on hold:', result);

      // Update local state
      this.updateCallState({
        isOnHold: true
      });
    } catch (error) {
      console.error('❌ [HoldCall] Failed to hold call:', error);
      throw error;
    }
  }

  async unholdCall(): Promise<void> {
    try {
      const callSid = this.callState.callSid;
      if (!callSid) {
        throw new Error('No active call to resume');
      }

      if (!this.callState.isOnHold) {
        console.log('📞 Call is not on hold');
        return;
      }

      console.log('▶️ [UnholdCall] Resuming call from hold...', { callSid });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${TWILIO_API_URL}/calls/${callSid}/unhold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resume call');
      }

      const result = await response.json();
      console.log('✅ [UnholdCall] Call resumed from hold:', result);

      // Update local state
      this.updateCallState({
        isOnHold: false
      });
    } catch (error) {
      console.error('❌ [UnholdCall] Failed to resume call:', error);
      throw error;
    }
  }

  toggleMute(): void {
    if (!this.call) return;

    this.call.mute(!this.callState.isMuted);
    this.updateCallState({ isMuted: !this.callState.isMuted });
  }

  // Toggle speaker
  toggleSpeaker(): void {
    this.updateCallState({ isSpeakerOn: !this.callState.isSpeakerOn });
    // Note: Speaker toggle is handled by the browser's audio output selection
  }

  // Hang up call — dismiss overlay immediately, tear down Twilio/backend in background.
  async hangUp(userId?: string): Promise<void> {
    if (this.hangupRequested) {
      console.log('📞 Hang up already in progress');
      return;
    }

    this.hangupRequested = true;
    this.hangupUiLockUntil = Date.now() + 3000;

    const snapshot = {
      channel: this.callState.channel,
      clientId: this.callState.clientId,
      appRingCallId: this.outboundAppRingCallId,
      status: this.callState.status,
      direction: this.callState.direction,
      isConnected: this.callState.isConnected,
      callSid: this.callState.callSid || (this as any).incomingCallData?.callSid,
      wasDialPrep: this.isOutboundDialPrep(),
    };

    try {
      console.log('📞 Hanging up call...');
      this.clearOutboundEndTimer();

      if (snapshot.direction === 'outbound' || snapshot.channel === 'app') {
        this.markOutboundPlacementCancelled();
      }

      const showOverlay =
        this.callState.status !== 'idle' || snapshot.wasDialPrep;

      // 1. Dismiss banner/overlay immediately (do not wait for Twilio idle).
      if (showOverlay) {
        this.detachCallListeners();
        this.endCall({ notifyAppCancel: false, skipTwilioRelease: true });
      }

      // 2. Release legs + notify backend without blocking the UI.
      void this.performHangupCleanup(snapshot, userId).finally(() => {
        this.hangupRequested = false;
      });
    } catch (error) {
      console.error('❌ Error hanging up call:', error);
      if (this.callState.status !== 'idle') {
        this.endCall({ notifyAppCancel: snapshot.channel === 'app', skipTwilioRelease: true });
      }
      void this.performHangupCleanup(snapshot, userId).finally(() => {
        this.hangupRequested = false;
      });
    }
  }

  private async performHangupCleanup(
    snapshot: {
      channel: CallState['channel'];
      clientId: string | null;
      appRingCallId: string | null;
      status: CallState['status'];
      direction: CallState['direction'];
      isConnected: boolean;
      callSid: string | null;
      wasDialPrep: boolean;
    },
    userId?: string,
  ): Promise<void> {
    const isRinging = snapshot.status === 'ringing' || snapshot.wasDialPrep;
    const isInboundRinging = isRinging && snapshot.direction === 'inbound';
    const isOutboundRinging = isRinging && snapshot.direction === 'outbound';
    const callSid = snapshot.callSid;
    const token = localStorage.getItem('token');
    const currentUserId = userId || this.getCurrentUserId();

    if (snapshot.status === 'idle' && !snapshot.wasDialPrep && !this.device?.isBusy) {
      return;
    }

    await this.releaseTwilioDeviceCalls({
      rejectInboundRinging: isInboundRinging || isOutboundRinging,
      maxWaitMs: 5000,
    });
    await this.waitForTwilioDeviceIdle(3000);

    if (callSid && currentUserId) {
      try {
        const response = await fetch(
          `${TWILIO_API_URL}/workers/${currentUserId}/hangup-call`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token ?? ''}`,
            },
            body: JSON.stringify({
              userId: currentUserId,
              callSid,
              isRinging,
            }),
          },
        );
        if (!response.ok) {
          console.warn('⚠️ Backend hangup failed after local disconnect');
        }
      } catch (error) {
        console.error('❌ Error calling backend hangup:', error);
      }
    }

    if (snapshot.channel === 'app' && snapshot.clientId) {
      await this.cancelAppRingForClient(snapshot.clientId, snapshot.appRingCallId);
    }

    if (this.device?.isBusy) {
      console.warn('📞 Device still busy after hangup — forcing flush');
      await this.flushTwilioDeviceIdle(3000);
    }
  }

  // Start call timer
  private startCallTimer(queueTime: number = 0): void {
    this.callTimer = {
      startTime: new Date(),
      queueTime,
      callTime: 0,
      totalTime: queueTime
    };

    // Start timer interval
    this.timerInterval = setInterval(() => {
      if (this.callTimer) {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - this.callTimer.startTime.getTime()) / 1000);
        
        this.callTimer.callTime = elapsed;
        this.callTimer.totalTime = this.callTimer.queueTime + elapsed;
        
        this.updateCallState({ callDuration: this.callTimer.totalTime });
        this.notifyTimerListeners(this.callTimer);
      }
    }, 1000);
  }

  /** Mobile client declined or ended ring before answer — tear down agent outbound UI immediately. */
  private handleClientDeclinedCall(data: { callId?: string; targetId?: string }): void {
    if (this.callState.direction === 'outbound' && this.callState.channel === 'app') {
      if (!this.matchesOutboundAppRing(data)) {
        console.log('📞 [Call] Ignoring client declined for unrelated/stale callId:', data?.callId);
        return;
      }
      if (this.callState.status === 'in-call' && this.callState.isConnected) {
        console.log('📞 [Call] Ignoring client declined — outbound app call already connected');
        return;
      }
      this.markOutboundPlacementCancelled();
    }

    console.log('📞 [Call] Client declined — stopping agent outbound session');
    this.clearOutboundEndTimer();

    const activeCall = this.call;
    if (activeCall) {
      this.call = null;
      void (async () => {
        try {
          if (typeof activeCall.reject === 'function' && !this.callState.isConnected) {
            await activeCall.reject();
          } else {
            await activeCall.disconnect();
          }
        } catch {
          /* ignore */
        }
      })();
    }

    if (this.incomingCallTimeout) {
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }

    this.stopRingingMusic();

    if (this.isOutboundDialPrep() || this.callState.status !== 'idle') {
      this.endCall();
    }
  }

  // End call and cleanup
  endCall(options?: { notifyAppCancel?: boolean; skipTwilioRelease?: boolean }): void {
    console.log('📞 Ending call and cleaning up...');

    this.clearOutboundEndTimer();
    this.outboundPrepStartedAt = null;
    this.outboundAppClientAnswered = false;
    this.acceptedLocally = false;

    // Only abort in-flight handleAppCall when agent explicitly hung up, not on passive cleanup.
    if (options?.notifyAppCancel || this.hangupRequested) {
      if (this.callState.direction === 'outbound' || this.callState.channel === 'app') {
        this.markOutboundPlacementCancelled();
      }
    }

    if (options?.notifyAppCancel && this.shouldNotifyAppCancelRing()) {
      void this.cancelAppRingIfNeeded();
    }

    if (!options?.skipTwilioRelease) {
      const lingeringCall = this.call;
      const wasOutboundRinging =
        this.callState.direction === 'outbound' && !this.callState.isConnected;
      this.call = null;
      if (lingeringCall) {
        void (async () => {
          // Use reject() for any ringing call (inbound or outbound) — reject() stops the ring
          // sound on both sides. disconnect() on an unaccepted incoming call does NOT stop
          // the Twilio SDK's built-in ring audio.
          const wasInboundRinging =
            this.callState.direction === 'inbound' && !this.callState.isConnected;
          try {
            if (
              typeof lingeringCall.reject === 'function' &&
              (wasOutboundRinging || wasInboundRinging)
            ) {
              await lingeringCall.reject();
            } else if (typeof lingeringCall.disconnect === 'function') {
              await lingeringCall.disconnect();
            }
          } catch {
            try {
              if (typeof lingeringCall.disconnect === 'function') {
                await lingeringCall.disconnect();
              }
            } catch {
              /* ignore */
            }
          }
          await this.releaseTwilioDeviceCalls({ maxWaitMs: 4000 });
        })();
      } else if (this.device?.isBusy) {
        void this.releaseTwilioDeviceCalls({ maxWaitMs: 4000 });
      }
    } else {
      this.call = null;
    }
    
    // Stop timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    // Stop ringing music
    this.stopRingingMusic();

    // Clear timeout
    if (this.incomingCallTimeout) {
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }

    this.callTimer = null;

    // Clear incoming call data and tracking
    (this as any).incomingCallData = null;
    this.lastProcessedCallSid = null;
    this.lastProcessedEventTime = 0;
    this.outboundAppRingCallId = null;
    
    const endedChannel = this.callState.channel;

    // Immediately update call state to idle (preserve channel for listeners that reload chat)
    this.updateCallState({
      status: 'idle',
      isConnected: false,
      isMuted: false,
      isSpeakerOn: true,
      isOnHold: false,
      callDuration: 0,
      callerInfo: null,
      callSid: null,
      direction: 'none',
      queueTime: 0,
      channel: endedChannel,
    });

    // Update agent status to available after call ends
    this.updateAgentStatusToAvailable();
  }

  /**
   * The agent answered this call on another device (mobile). Tear down only the
   * local browser ring/UI — reject this tab's (already-forked) Twilio leg and go
   * idle. Deliberately does NOT flip the worker to "available" (the agent is now
   * busy on the other device) and does NOT send any server-side hangup.
   */
  private dismissRingBecauseAnsweredElsewhere(): void {
    if (this.callState.status !== 'ringing') {
      return;
    }

    console.log('📞 [answeredElsewhere] Dismissing local ring — agent answered on another device');

    if (this.incomingCallTimeout) {
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }

    this.stopRingingMusic();

    // Reject only this browser's forked leg so the Twilio SDK ring stops here.
    // The bridged leg on the other device is a separate leg and is unaffected.
    const lingeringCall = this.call;
    this.call = null;
    if (lingeringCall) {
      try {
        if (typeof lingeringCall.reject === 'function') {
          lingeringCall.reject();
        } else if (typeof lingeringCall.disconnect === 'function') {
          lingeringCall.disconnect();
        }
      } catch (err) {
        console.warn('📞 [answeredElsewhere] Error rejecting local Twilio leg', err);
      }
    }

    (this as any).incomingCallData = null;
    this.lastProcessedCallSid = null;
    this.lastProcessedEventTime = 0;
    this.acceptedLocally = false;

    const endedChannel = this.callState.channel;
    this.updateCallState({
      status: 'idle',
      isConnected: false,
      isMuted: false,
      isSpeakerOn: true,
      isOnHold: false,
      callDuration: 0,
      callerInfo: null,
      callSid: null,
      direction: 'none',
      queueTime: 0,
      channel: endedChannel,
    });
  }

  private async cancelAppRingIfNeeded(): Promise<void> {
    const { channel, clientId } = this.callState;
    if (channel !== 'app' || !clientId) {
      return;
    }
    await this.cancelAppRingForClient(clientId, this.outboundAppRingCallId);
  }

  // Update agent status to available
  private async updateAgentStatusToAvailable(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const userId = this.getCurrentUserId();
      if (!userId) {
        console.warn('⚠️ updateAgentStatusToAvailable: No user ID found');
        return;
      }

      const response = await fetch(`${TWILIO_API_URL}/workers/${userId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: userId,
          status: 'idle'
        })
      });

      if (response.ok) {
      console.log('✅ Agent status updated to idle after call end');
      } else {
        console.error('❌ Failed to update agent status to available');
      }
    } catch (error) {
      console.error('❌ Error updating agent status to available:', error);
    }
  }

  // Update call state and notify listeners
  private updateCallState(updates: Partial<CallState>): void {
    const previousStatus = this.callState.status;

    // After Hang Up, ignore late socket/device events that would re-show the overlay.
    const hangupUiLocked = this.hangupRequested || Date.now() < this.hangupUiLockUntil;
    if (hangupUiLocked) {
      const nextStatus = updates.status ?? previousStatus;
      if (nextStatus === 'ringing' || nextStatus === 'in-call') {
        return;
      }
      if (updates.isConnected === true && previousStatus === 'idle') {
        return;
      }
    }

    const isSignificantChange = 
      updates.status !== undefined || 
      updates.isConnected !== undefined || 
      updates.isOnHold !== undefined ||
      updates.callSid !== undefined ||
      updates.clientId !== undefined;
    
    // Only log significant changes (not duration ticks)
    if (isSignificantChange) {
      console.log('🔄 [updateCallState] State change:', {
        updates,
        previousStatus,
        newStatus: updates.status || previousStatus
      });
    }
    
    this.callState = { ...this.callState, ...updates };
    
    this.notifyCallStateListeners();
  }

  private socketClientType(): 'web' | 'phone' {
    return this.agentClientType;
  }

  // Notify call state listeners
  private notifyCallStateListeners(): void {
    this.callStateListeners.forEach((listener) => {
      try {
        listener(this.callState);
      } catch (error) {
        console.error('❌ Error in call state listener:', error);
      }
    });
    if (isAgentPhoneWindow()) {
      publishCallState(this.callState);
    }
  }

  // Notify timer listeners
  private notifyTimerListeners(timer: CallTimer): void {
    this.timerListeners.forEach(listener => {
      try {
        listener(timer);
      } catch (error) {
        console.error('❌ Error in timer listener:', error);
      }
    });
  }

  // Fetch task details from backend to get correct clientPhone
  private async fetchTaskDetailsForCall(callSid: string): Promise<void> {
    try {
      console.log('🔍 [fetchTaskDetailsForCall] Fetching task details for callSid:', callSid);
      
      // Try to get from active tasks endpoint
      const response = await fetch(`${TWILIO_API_URL}/contact-center/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [fetchTaskDetailsForCall] Tasks received:', data);
        
        const tasks = data.tasks || [];
        const task = tasks.find((t: any) => t.callSid === callSid || t.taskSid === callSid);
        
        if (task) {
          const clientPhone = task.clientPhone;
          const clientId = task.clientId;
          const clientName = task.clientName;
          
          if (clientPhone && clientPhone !== '+14377478553' && clientPhone !== '+14374948464') {
            console.log('✅ [fetchTaskDetailsForCall] Found correct clientPhone from tasks:', clientPhone);
            this.updateCallState({
              clientPhone,
              clientId: clientId || this.callState.clientId,
              clientName: clientName || this.callState.clientName,
              callerInfo: this.callState.callerInfo ? {
                ...this.callState.callerInfo,
                phone: clientPhone
              } : { name: clientName || 'Caller', phone: clientPhone }
            });
          } else {
            console.warn('⚠️ [fetchTaskDetailsForCall] Task found but clientPhone is Twilio number or missing:', clientPhone);
          }
        } else {
          console.warn('⚠️ [fetchTaskDetailsForCall] Task not found in active tasks for callSid:', callSid);
        }
      } else {
        console.warn('⚠️ [fetchTaskDetailsForCall] Failed to fetch tasks:', response.status);
      }
    } catch (error) {
      console.error('❌ [fetchTaskDetailsForCall] Error fetching task details:', error);
    }
  }

  // Add call state listener
  addCallStateListener(listener: (state: CallState) => void): void {
    console.log('🔍 addCallStateListener: Adding listener, current count:', this.callStateListeners.length);
    this.callStateListeners.push(listener);
    console.log('🔍 addCallStateListener: New count:', this.callStateListeners.length);
  }

  // Remove call state listener
  removeCallStateListener(listener: (state: CallState) => void): void {
    console.log('🔍 removeCallStateListener: Removing listener, current count:', this.callStateListeners.length);
    const index = this.callStateListeners.indexOf(listener);
    if (index > -1) {
      this.callStateListeners.splice(index, 1);
      console.log('🔍 removeCallStateListener: Removed listener, new count:', this.callStateListeners.length);
    } else {
      console.log('🔍 removeCallStateListener: Listener not found in array');
    }
  }

  // Add timer listener
  addTimerListener(listener: (timer: CallTimer) => void): void {
    this.timerListeners.push(listener);
  }

  // Remove timer listener
  removeTimerListener(listener: (timer: CallTimer) => void): void {
    const index = this.timerListeners.indexOf(listener);
    if (index > -1) {
      this.timerListeners.splice(index, 1);
    }
  }

  // Get current call state
  getCallState(): CallState {
    return { ...this.callState };
  }


  // Get current timer
  getCallTimer(): CallTimer | null {
    return this.callTimer ? { ...this.callTimer } : null;
  }

  // Check if device is ready
  isDeviceReady(): boolean {
    return this.device !== null && this.device.state === 'registered';
  }


  // Clear RINGING STATE - when call is no longer ringing
  clearRingingState(): void {
    if (this.callState.direction === 'outbound') {
      console.log('🧹 RINGING STATE: Skipped — outbound call active');
      return;
    }

    console.log('🧹 RINGING STATE: Starting to clear ringing state...');
    console.log('🧹 RINGING STATE: Current state before clearing:', this.callState.status);
    
    // Clear incoming call data
    (this as any).incomingCallData = null;
    
    // Stop ringing music
    this.stopRingingMusic();
    
    // Clear timeout
    if (this.incomingCallTimeout) {
      clearTimeout(this.incomingCallTimeout);
      this.incomingCallTimeout = null;
    }
    
    // Reset to idle state
    this.updateCallState({
      status: 'idle',
      isConnected: false,
      callerInfo: null,
      callSid: null,
      direction: 'none',
      queueTime: 0,
      clientId: null,
      clientName: null
    });
    
    console.log('🧹 RINGING STATE: Ringing state cleared, reset to idle');
  }


  // Start ringing music
  private startRingingMusic(): void {
    this.stopRingingMusic(); // Stop any existing ringing
    
    try {
      this.ringingAudio = new Audio('/ringtone.mp3'); // You can use any ringtone file
      this.ringingAudio.loop = true;
      this.ringingAudio.volume = 0.5;
      this.ringingAudio.play().catch(error => {
        console.log('🔇 Could not play ringing music:', error);
      });
    } catch (error) {
      console.log('🔇 Could not create ringing audio:', error);
    }
  }

  // Stop ringing music
  private stopRingingMusic(): void {
    console.log('🔇 stopRingingMusic: Starting to stop ringing music...');
    console.log('🔇 stopRingingMusic: Current ringing audio state:', {
      hasRingingAudio: !!this.ringingAudio,
      ringingAudioState: this.ringingAudio ? {
        paused: this.ringingAudio.paused,
        currentTime: this.ringingAudio.currentTime,
        duration: this.ringingAudio.duration,
        volume: this.ringingAudio.volume,
        loop: this.ringingAudio.loop
      } : null
    });
    
    if (this.ringingAudio) {
      console.log('🔇 stopRingingMusic: Found ringing audio, attempting to stop...');
      try {
        this.ringingAudio.pause();
        console.log('🔇 stopRingingMusic: Audio paused successfully');
        
        this.ringingAudio.currentTime = 0;
        console.log('🔇 stopRingingMusic: Audio currentTime reset to 0');
        
        this.ringingAudio = null;
        console.log('🔇 stopRingingMusic: Audio reference cleared');
        
        console.log('🔇 stopRingingMusic: Ringing music stopped successfully');
      } catch (error) {
        console.error('🔇 stopRingingMusic: Error stopping ringing audio:', error);
      }
    } else {
      console.log('🔇 stopRingingMusic: No ringing audio found to stop');
    }
    
    // Also stop incoming sound if it exists
    // Commented out - using Twilio's default incoming sound
    // if ((this as any).incomingSound) {
    //   console.log('🔇 stopRingingMusic: Also stopping incoming sound...');
    //   try {
    //     (this as any).incomingSound.pause();
    //     (this as any).incomingSound.currentTime = 0;
    //     (this as any).incomingSound = null;
    //     console.log('🔇 stopRingingMusic: Incoming sound stopped successfully');
    //   } catch (error) {
    //     console.error('🔇 stopRingingMusic: Error stopping incoming sound:', error);
    //   }
    // }
  }



  // Play incoming call sound
  // Commented out - using Twilio's default incoming sound
  // private playIncomingSound(): void {
  //   try {
  //     console.log('🔊 playIncomingSound: Starting to play incoming call sound...');
  //     console.log('🔊 playIncomingSound: Current ringing audio state:', {
  //       hasRingingAudio: !!this.ringingAudio,
  //       ringingAudioState: this.ringingAudio ? {
  //         paused: this.ringingAudio.paused,
  //         currentTime: this.ringingAudio.currentTime,
  //         duration: this.ringingAudio.duration,
  //         volume: this.ringingAudio.volume,
  //         loop: this.ringingAudio.loop
  //       } : null
  //     });
  //     
  //     // Stop any existing sound first
  //     this.stopRingingMusic();
  //     
  //     const audio = new Audio('/sounds/incoming_sound.wav');
  //     audio.loop = true;
  //     audio.volume = 0.7;
  //     
  //     console.log('🔊 playIncomingSound: Created audio element:', {
  //       src: audio.src,
  //       loop: audio.loop,
  //       volume: audio.volume,
  //       paused: audio.paused
  //     });
  //     
  //     // Store reference to stop it later
  //     (this as any).incomingSound = audio;
  //     this.ringingAudio = audio;
  //     
  //     audio.play().then(() => {
  //       console.log('🔔 playIncomingSound: Audio play() promise resolved - sound should be playing');
  //       console.log('🔔 playIncomingSound: Audio state after play:', {
  //         paused: audio.paused,
  //         currentTime: audio.currentTime,
  //         duration: audio.duration,
  //         volume: audio.volume,
  //         loop: audio.loop
  //       });
  //     }).catch(error => {
  //       console.warn('⚠️ playIncomingSound: Audio play() promise rejected:', error);
  //       console.warn('⚠️ playIncomingSound: Audio state after error:', {
  //         paused: audio.paused,
  //         currentTime: audio.currentTime,
  //         duration: audio.duration,
  //         volume: audio.volume,
  //         loop: audio.loop
  //       });
  //     });
  //     
  //     console.log('🔊 playIncomingSound: Final state after setup:', {
  //       hasIncomingSound: !!(this as any).incomingSound,
  //       hasRingingAudio: !!this.ringingAudio,
  //       ringingAudioPaused: this.ringingAudio?.paused,
  //       ringingAudioCurrentTime: this.ringingAudio?.currentTime
  //     });
  //   } catch (error) {
  //     console.warn('⚠️ playIncomingSound: Exception caught:', error);
  //   }
  // }

  // Stop incoming call sound
  // Commented out - using Twilio's default incoming sound
  // private stopIncomingSound(): void {
  //   try {
  //     const audio = (this as any).incomingSound;
  //     if (audio) {
  //       audio.pause();
  //       audio.currentTime = 0;
  //       (this as any).incomingSound = null;
  //       console.log('🔇 Stopped incoming call sound');
  //     }
  //   } catch (error) {
  //     console.warn('⚠️ Error stopping incoming sound:', error);
  //   }
  // }

  // Get current user ID from localStorage or context
  private getCurrentUserId(): string | null {
    if (this.agentId) {
      return this.agentId;
    }
    const userId = localStorage.getItem('userId');
    if (userId) {
      return userId;
    }
    // Fallback to get user ID from localStorage (set by auth system)
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user._id || user.id || null;
      } catch (error) {
        console.error('❌ Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  // Reset service to initial state
  reset(): void {
    console.log('🔄 Resetting WebRTC Call Service to initial state');
    this.endCall();
    // Commented out - using Twilio's default incoming sound
    // this.stopIncomingSound();
    this.isInitialized = false;
    this.lastProcessedCallSid = null;
    this.lastProcessedEventTime = 0;
  }

  // Cleanup
  destroy(): void {
    console.log('🔧 Destroying WebRTCCallService...');
    
    // Cleanup resilience handlers first
    this.cleanupResilienceHandlers();
    
    // End any active call
    this.endCall();
    // Commented out - using Twilio's default incoming sound
    // this.stopIncomingSound();
    
    // Send graceful leave if connected
    if (this.socket?.connected && this.agentId) {
      console.log('👋 Sending agent:leave before destroy');
      this.socket.emit('agent:leave', { userId: this.agentId });
    }
    
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.isInitialized = false;
    this.agentId = null;
    this.agentName = null;
    this.lastProcessedCallSid = null;
    this.lastProcessedEventTime = 0;
    
    console.log('✅ WebRTCCallService destroyed');
  }
}

// Export singleton instance
export const webRTCCallService = WebRTCCallService.getInstance();
